import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Spinner, sparkToast, AlertBanner } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType, isGenerativeTask, DatasetOrigin } from '@/shared/domain/taskTypes';
import { labelPriorityOptimizeOnLabel, missingOptimizeOnLabel } from '@/shared/domain/labelPriority';
import OptimizeOnLabelMissingModal from '@/shared/components/OptimizeOnLabelMissingModal';
import {
    fetchFileLabelsFromContent,
    fetchFileLabelsWithUpload,
    applyExampleFieldToDataset,
    applyLabelColumnToDataset,
    clearLabelColumnFromDataset,
    MAX_PREVIEW_FILE_SIZE_BYTES,
    acceptAttributeForTaskType,
} from '@/features/datasets/utils/datasetUploadFlow';
import {
    EMPTY_VALUE_KIND_RESPONSE,
    EMPTY_VALUE_KIND_TRAJECTORY,
    emptyScanKey,
    scanUploadEntriesForEmptyField,
    shouldShowEmptyResponsesSection,
    shouldShowEmptyTrajectoriesSection,
} from '@/features/datasets/utils/emptyFieldScan';
import {
    EMPTY_TRAJECTORIES_DEFAULT,
    isEmptyTrajectoryGenerateWithAi,
} from '@/features/datasets/utils/emptyTrajectoriesHandling';
import {
    fieldValuesFromUploadEntry,
    sampleRecordsFromUploadEntry,
    uploadEntryHasPreviewSamples,
} from '@/features/datasets/utils/filePreviewUtils';
import { guessLabelsDelimiterFromEntries } from '@/features/datasets/utils/labelsDelimiter';
import {
    LANGUAGE_DETECTION_SAMPLE_SIZE,
    significantLanguagesFromTexts,
} from '@/features/datasets/utils/detectLanguagesFromSamples';
import {
    fetchLibraryDatasetHasEmptyLabels,
    fetchLibraryDatasetHasEmptyTrajectories,
    fetchLibraryDatasetSample,
} from '@/features/datasets/utils/libraryPickDefaults';
import {
    fetchHfDatasetEmptyValues,
    fetchHfDatasetLabels,
} from '@/features/datasets/utils/huggingFaceDatasetApi';
import LanguageSelector from '@/features/tasks/components/LanguageSelector';
import { useUsecaseFileRemoval } from '@/features/datasets/hooks/useUsecaseFileRemoval';
import { stripCloneSuffix } from '@/features/datasets/utils/datasetCloneNames';
import {
    LLM_CREATED_DATA_CREATION,
    applyAiGeneratedColumnDefaults,
    dataCreationForUi,
    identityLabelMappings,
    isAiGeneratedDataset,
    isAiGeneratedDatasetName,
    isAiGeneratedUploadEntry,
    knownLabelsForAiGeneration,
} from '@/features/datasets/utils/aiGeneratedDatasetDefaults';
import {
    fallbackLabelFromStored,
    fallbackPayloadFromSelect,
    isFallbackSentinel,
} from '../businessGoals/fallbackLabel';
import { sourceFieldsFromDatasetInfoDoc } from '@/features/datasets/utils/datasetSourceConfig';
import { primaryFileEntry, isPendingUploadEntry, emptyFileEntry, useWizardDataUpload } from './useWizardDataUpload';
import DataUploadSection from './DataUploadSection';
import DatasetDetailsForm, { dynamicFieldsFromTemplate } from './DatasetDetailsForm';
import { isAgenticAutonomyLevel } from '../businessGoals/autonomyLevels';
import {
    TRAJECTORY_SAMPLE_ROWS,
    emptyTrajectoryDetails,
    trajectoryDetailsErrors,
    trajectoryMappingFromDetails,
    trajectoryYesDetails,
} from './trajectory/trajectoryMapping';
import ContinueWithoutBenchmarkModal from './ContinueWithoutBenchmarkModal';
import UnknownLabelsWarningModal from './UnknownLabelsWarningModal';
import NoProviderWarningModal from './NoProviderWarningModal';
import {
    isLabelExtractionPending,
    unknownLabelSources,
} from './labelMappingWarnings';
import { detailsWantAiGeneration, hasNoConfiguredProviders } from './noProviderWarning';
import { stashDataCollectionState } from './settingsReturnStash';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import WizardStepHeading from '../steps/WizardStepHeading';
import {
    emptyResponseDefault,
    isEmptyGenerateWithAi,
} from '@/features/datasets/utils/emptyResponsesHandling';
import {
    NO_LABELS_MODE_UNLABELED,
    shouldDeferLabelGeneration,
    shouldSubmitAiLabelMappings,
} from '@/features/datasets/utils/hasLabelsHandling';
import { buildTaskLabelMatcher } from '@/features/datasets/utils/labelFuzzyMatching';
import { defaultLabelColumn, findColumnFuzzy } from './columnDefaults';
import { labelColumnSyncAction } from './labelColumnSync';
import { syncTaskLabelCatalog } from './taskLabelCatalogSync';
import {
    fileDetailsHeading,
    fileDisplayName,
    shouldHideShareDetailsToggle,
    shouldSplitFileDetails,
} from './fileDetailsLayout';
import {
    COLUMN_OVERLAP,
    classifyColumnOverlap,
    crossTargetColumnsMismatch,
    normalizeColumnList,
    partitionEntriesByColumnOverlap,
    shouldAbortUploadForNoSharedColumns,
    showNoSharedColumnsToast,
    SHARED_COLUMNS_WARNING,
    SHARED_COLUMNS_CARD_ERROR,
} from '@/features/datasets/utils/sharedFileColumns';
import '../creation-wizard.css';

/** Build the wizard-shaped form blob from an existing usecase (task-page modal). */
export function formFromUsecase(usecase) {
    if (!usecase) return null;
    const isMultilabel = usecase.is_multilabel === true;
    return {
        taskType: usecase.task_type,
        classificationType: isMultilabel ? 'multi' : 'single',
        autonomyLevel: usecase.autonomy_level ?? null,
        fallbackLabel: fallbackLabelFromStored({
            isClassification: usecase.task_type === TaskType.classification,
            isMultilabel,
            rejectionLabelName: usecase.rejection_label_name,
            confidenceRejectionEnabled: usecase.confidence_rejection_enabled,
        }),
        labels:
            usecase.task_type === TaskType.NER
                ? usecase.manually_added_entities || []
                : usecase.task_type === TaskType.classification
                  ? usecase.manually_added_labels || []
                  : [],
        // Business Goals choice, so the Add-Data modal can refuse a "Next"
        // that would leave it pointing at a label the task no longer has.
        labelPriority: usecase.label_priority || null,
    };
}

/** Map AddDataModal `uploadTarget` to which upload cards to show. */
export function visibleTargetsFromUploadTarget(uploadTarget) {
    if (uploadTarget === 'benchmark') return ['benchmark'];
    if (uploadTarget === 'dataset') return ['dataset'];
    return ['dataset', 'benchmark'];
}

/** True when the slot has a pending File or post-upload metadata. */
const slotHasLocalData = (slot) =>
    (slot?.files || []).some((entry) => entry.file || entry.uploadName);

export const SHARE_DETAILS_LABEL = 'Dataset and Benchmark share the same details';

/** Checkbox + title only — empty space on the row is not a hit target. */
export function ShareDetailsToggle({ checked, disabled, onChange }) {
    return (
        <div className="cw-dc-share-details-toggle">
            <Checkbox
                label=""
                status={checked ? 'active' : 'inactive'}
                disabled={disabled}
                aria-label={SHARE_DETAILS_LABEL}
                onChange={(e) => onChange(e.target.checked)}
            />
            <button
                type="button"
                className="cw-dc-share-details-title"
                disabled={disabled}
                onClick={() => onChange(!checked)}
            >
                {SHARE_DETAILS_LABEL}
            </button>
        </div>
    );
}

function slotCoversDatasetName(files, name) {
    if (!name) return false;
    const stripped = stripCloneSuffix(name) || name;
    return (files || []).some((entry) => {
        const keys = [entry.uploadName, entry.datasetName].filter(Boolean);
        return keys.some((key) => {
            const keyStripped = stripCloneSuffix(key) || key;
            return key === name || key === stripped || keyStripped === stripped;
        });
    });
}

function fetchUsecaseDatasetInfos(usecase) {
    return new Promise((resolve) => {
        if (!usecase?.client_id || !usecase?._id) {
            resolve([]);
            return;
        }
        data_from_server_post(
            'datasets-and-benchmarks-meta-data',
            { client_id: usecase.client_id, llm_usecase_id: usecase._id },
            (result) => resolve(result?.datasets_info || []),
            () => resolve([])
        );
    });
}

function overlapForFiles(files) {
    const list = files || [];
    if (list.some((entry) => entry.columnsLoading)) {
        return { status: COLUMN_OVERLAP.unknown, shared: [] };
    }
    return classifyColumnOverlap(list.map((entry) => entry.columns));
}

/** Case-sensitive union of label lists, preserving first-seen order. */
const unionLabels = (lists) => {
    const seen = new Set();
    const result = [];
    for (const list of lists) {
        for (const label of list || []) {
            if (seen.has(label)) continue;
            seen.add(label);
            result.push(label);
        }
    }
    return result;
};

export const emptyDetails = () => ({
    dataCreation: null,
    exampleColumn: null,
    customInstructions: false,
    promptTemplate: '',
    /** Primary model input chosen in the Map prompt template modal. */
    primaryModelInput: null,
    /** Custom fields defined in the modal: { fieldName: originalText }. */
    promptCustomFields: {},
    /** undefined = untouched, '' = opted out / no column, string = column name. */
    labelColumn: undefined,
    /** Default ON — "Does your data contain labels/entities/summaries/outputs?" */
    hasLabels: true,
    /** When hasLabels is false: generate_with_ai (default) | unlabeled. */
    noLabelsMode: 'generate_with_ai',
    /** AI catalog rows when generate_with_ai (classification / NER). */
    aiLabels: [],
    extractedLabels: [],
    labelMappings: {},
    labelsLoading: false,
    /**
     * Multilabel classification labels delimiter (';', ',', ' ', '|', '\t',
     * 'list'). Auto-detected from the first records of the label column,
     * user-editable. null = not determined yet — extraction waits for it.
     */
    labelsDelimiter: null,
    /** Fallback label: Best guess, No predictions, a label name, or null (unset). */
    rejectionLabel: null,
    /**
     * How empty response cells are handled on import.
     * Multilabel default: none_selected; otherwise unlabeled.
     * generate_with_ai keeps existing responses and fills blanks via LABEL_ONLY.
     * Seeded when classification type / task type is known via effects.
     */
    emptyMultilabelHandling: null,
    /** Agentic tasks: "Does your data contains trajectories?" + field mapping. */
    ...emptyTrajectoryDetails(),
});

/** Details keys whose own inline error is cleared the moment they are patched. */
const ERROR_CLEARING_DETAILS_FIELDS = [
    'exampleColumn',
    'labelColumn',
    'rejectionLabel',
    'labelsDelimiter',
    'trajectoryColumn',
    'trajectoryRoleKey',
    'trajectoryContentKey',
    'trajectoryToolCallNameKey',
];

const isDetailsConfigured = (details) =>
    Boolean(
        details &&
            (details.exampleColumn ||
                details.labelColumn ||
                details.dataCreation ||
                details.promptTemplate ||
                (details.extractedLabels || []).length)
    );

/**
 * The example_dynamic_field a saved prompt mapping resolves to: the picked
 * Primary model input, with the legacy-draft fallback chain (drafts saved
 * before the primary existed fall back to `example`, then the first template
 * field). Null when no template is saved.
 */
const exampleDynamicFieldOf = (details) => {
    const template = details?.customInstructions ? (details.promptTemplate || '').trim() : '';
    const dynamicFields = dynamicFieldsFromTemplate(template);
    if (!template || !dynamicFields.length) return null;
    return details.primaryModelInput && dynamicFields.includes(details.primaryModelInput)
        ? details.primaryModelInput
        : dynamicFields.includes('example')
          ? 'example'
          : dynamicFields[0];
};

const applyExtractedLabels = (prevDetails, labels, matchTaskLabel) => {
    const prevMappings = prevDetails?.labelMappings || {};
    return {
        ...prevDetails,
        extractedLabels: labels,
        labelMappings: Object.fromEntries(
            labels.map((label) => [
                label,
                prevMappings[label] ?? matchTaskLabel(label) ?? label,
            ])
        ),
        labelsLoading: false,
    };
};

/** Order-independent equality of extracted label name sets. */
const extractedLabelsEqual = (a, b) => {
    const setA = new Set(a || []);
    const setB = new Set(b || []);
    if (setA.size !== setB.size) return false;
    for (const label of setA) {
        if (!setB.has(label)) return false;
    }
    return true;
};

const SHARE_DETAILS_LABELS_MISMATCH_ERROR =
    'Dataset and Benchmark have different extracted labels. Configure their details separately.';

export const SHARE_DETAILS_COLUMNS_MISMATCH_ERROR =
    'Dataset and Benchmark have no columns in common. Configure their details separately.';

/**
 * Indicative toast copy for a failing "Next" validation, matching the
 * Business Goals step (Figma 3423-15453): a single failing field gets a
 * specific message; several failing fields get one generic message instead
 * of arbitrarily naming just the first field.
 */
const validationToast = (validationErrors, { isNer, isGenerative, isContentGeneration }) => {
    const detailErrors = Object.values(validationErrors.details || {}).flatMap((fields) =>
        Object.entries(fields)
    );
    const failingCount = (validationErrors.dataset ? 1 : 0) + detailErrors.length;
    if (failingCount > 1) {
        return {
            title: 'Missing required fields',
            content: 'Please complete the highlighted fields to continue',
        };
    }
    if (validationErrors.dataset) {
        return {
            title: validationErrors.dataset.toLowerCase().includes('benchmark')
                ? 'Benchmark required'
                : 'Dataset required',
            content: validationErrors.dataset,
        };
    }
    const [field, content] = detailErrors[0];
    if (field === 'exampleColumn') return { title: 'Example field required', content };
    if (field === 'labelsDelimiter') return { title: 'Labels delimiter required', content };
    if (field === 'trajectoryColumn') return { title: 'Trajectory field required', content };
    if (field === 'trajectoryRoleKey') return { title: 'Role field required', content };
    if (field === 'trajectoryContentKey') return { title: 'Content field required', content };
    if (field === 'trajectoryRoleMapping') return { title: 'Role mapping required', content };
    // labelColumn — the message depends on the task type and failure mode.
    if (content.startsWith('Add at least one')) {
        return { title: isNer ? 'Entity types required' : 'Labels required', content };
    }
    if (isGenerative) {
        return {
            title: isContentGeneration ? 'Output field required' : 'Summary field required',
            content,
        };
    }
    return { title: 'Labels column required', content };
};

/**
 * Shared Data Collection UI used by the create-task wizard step and the
 * task-page "Upload Data" modal. Dataset/benchmark upload cards drive the
 * shared two-phase upload flow, with languages and per-file details forms.
 *
 * `variant="wizard"` shows Back/Next and the continue-without-benchmark gate.
 * `variant="modal"` shows a Done button and skips the benchmark gate so users
 * can add only dataset or only benchmark from Data Overview.
 *
 * Library / Hugging Face / Generate-with-AI are opened by the parent via the
 * `onOpen*` callbacks — keeps this panel free of a circular import with
 * AddDataModal.
 */
const DataCollectionPanel = ({
    usecase,
    /** Wizard-only: shown above the "Data Collection" step title. */
    taskName = null,
    form,
    onFormChange,
    onBack,
    onNext,
    onComplete,
    stateCacheRef,
    variant = 'wizard',
    visibleTargets = ['dataset', 'benchmark'],
    initialDroppedFiles = null,
    onInitialDroppedFilesConsumed,
    /** Modal-only: skip Data Upload cards (OS picker / drop already chose files). */
    hideUploadSection = false,
    /** Modal-only: close without submitting (e.g. dropped files share no columns). */
    onDismiss,
    onOpenLibrary,
    onOpenHuggingFace,
    onOpenGenerateAi,
    onOpenRetrievalAgent,
    /** `{ picks: [{ target, option }] }` from LibraryPickModal — consumed once to autofill. */
    pendingLibraryPick = null,
    onPendingLibraryPickConsumed,
    /** Hugging Face dataset + splits from the selection-only modal. */
    pendingHuggingFacePick = null,
    onPendingHuggingFacePickConsumed,
}) => {
    const { refreshUsecases } = useContext(GlobalContext);
    const { confirmRemoval, removeFromUsecase } = useUsecaseFileRemoval();
    const navigate = useNavigate();
    const location = useLocation();
    // A Generate-with-AI selection (no-labels mode or empty-response
    // handling) without a configured model provider blocks Next with
    // NoProviderWarningModal — "Add credentials" deep-links to Settings >
    // Model Providers.
    const { availableProviders, isLoading: isProvidersCatalogLoading } = useCommercialModels();
    const hasNoProviders = hasNoConfiguredProviders(availableProviders, isProvidersCatalogLoading);
    const isModal = variant === 'modal';
    const showDatasetCard = visibleTargets.includes('dataset');
    const showBenchmarkCard = visibleTargets.includes('benchmark');

    const taskType = form.taskType;
    const isClassification = taskType === TaskType.classification;
    const isNer = taskType === TaskType.NER;
    const isGenerative = isGenerativeTask(taskType);
    const isContentGeneration = taskType === TaskType.contentGeneration;
    const isSingleLabel = isClassification && form.classificationType === 'single';
    const isMultiLabel = isClassification && form.classificationType === 'multi';
    // Agentic (autonomy level ≠ 1): json/jsonl/parquet only, agentic
    // templates and the trajectories question. The wizard form carries the
    // level; the task-page modal falls back to the saved usecase.
    const isAgentic = isAgenticAutonomyLevel(form.autonomyLevel ?? usecase?.autonomy_level);

    // Snapshot of this step's state from the last time it was mounted — the
    // step unmounts on Back/Next, so picked files and details are restored
    // from the parent-held cache instead of starting from scratch.
    const cached = stateCacheRef?.current;

    const upload = useWizardDataUpload({
        usecase,
        taskType,
        initialSlots: cached?.slots,
        isAgentic,
    });
    const { slots } = upload;

    const [languages, setLanguages] = useState(
        cached?.languages ?? (usecase?.task_languages?.length ? usecase.task_languages : ['en'])
    );
    const [shareDetails, setShareDetails] = useState(cached?.shareDetails ?? true);
    const [detailsByTarget, setDetailsByTarget] = useState(
        cached?.detailsByTarget ?? {
            dataset: emptyDetails(),
            benchmark: emptyDetails(),
        }
    );
    const [detailsByFileId, setDetailsByFileId] = useState(cached?.detailsByFileId ?? {});
    const [shareDetailsError, setShareDetailsError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showBenchmarkModal, setShowBenchmarkModal] = useState(false);
    const [showUnknownLabelsModal, setShowUnknownLabelsModal] = useState(false);
    const [showNoProviderModal, setShowNoProviderModal] = useState(false);
    // `{ label, taskLabels }` while the "label to optimize on isn't in this
    // task" blocker is open (Business Goals choice vs. the labels edited here).
    const [optimizeOnMismatch, setOptimizeOnMismatch] = useState(null);
    const [errors, setErrors] = useState({});
    const [columnErrorByTarget, setColumnErrorByTarget] = useState({
        dataset: null,
        benchmark: null,
    });
    const overlapRejectRef = useRef(new Set());
    const addFilesInputRef = useRef(null);
    const addFilesTargetRef = useRef('dataset');

    const datasetFiles = slots.dataset?.files || [];
    const benchmarkFiles = slots.benchmark?.files || [];
    const datasetCount = datasetFiles.length;
    const benchmarkCount = benchmarkFiles.length;
    const hasBenchmarkAttached = Boolean(
        slotHasLocalData(slots.benchmark) || (usecase?.benchmarks || []).length > 0
    );
    const splitFileDetails = shouldSplitFileDetails({ datasetCount, benchmarkCount });
    const hideShareDetailsToggle = shouldHideShareDetailsToggle({
        datasetCount,
        hasBenchmark: hasBenchmarkAttached,
    });

    const detailsByTargetRef = useRef(detailsByTarget);
    detailsByTargetRef.current = detailsByTarget;
    const detailsByFileIdRef = useRef(detailsByFileId);
    detailsByFileIdRef.current = detailsByFileId;

    const openLibrary = (target = null) => {
        onOpenLibrary?.(target);
    };

    const clearDetailsFieldError = useCallback((target, field) => {
        setErrors((prev) => {
            if (!prev.details?.[target]?.[field]) return prev;
            const nextTarget = { ...prev.details[target] };
            delete nextTarget[field];
            const nextDetails = { ...prev.details };
            if (Object.keys(nextTarget).length) nextDetails[target] = nextTarget;
            else delete nextDetails[target];
            const next = { ...prev };
            if (Object.keys(nextDetails).length) next.details = nextDetails;
            else delete next.details;
            return next;
        });
    }, []);

    // Details fields whose inline error should disappear as soon as the user
    // edits them (the Next-time validation re-adds them if still invalid).
    // Trajectory keys map onto the errors produced by `trajectoryDetailsErrors`.
    const clearPatchedFieldErrors = useCallback(
        (target, patch) => {
            for (const field of ERROR_CLEARING_DETAILS_FIELDS) {
                if (field in patch) clearDetailsFieldError(target, field);
            }
            if ('trajectoryRoleMapping' in patch || 'trajectoryRoleValues' in patch) {
                clearDetailsFieldError(target, 'trajectoryRoleMapping');
            }
        },
        [clearDetailsFieldError]
    );

    const patchDetails = useCallback(
        (target, patch) => {
            setDetailsByTarget((prev) => ({ ...prev, [target]: { ...prev[target], ...patch } }));
            clearPatchedFieldErrors(target, patch);
        },
        [clearPatchedFieldErrors]
    );

    const patchFileDetails = useCallback(
        (fileId, patch) => {
            if (!fileId) return;
            setDetailsByFileId((prev) => ({
                ...prev,
                [fileId]: { ...(prev[fileId] || emptyDetails()), ...patch },
            }));
            clearPatchedFieldErrors(fileId, patch);
        },
        [clearPatchedFieldErrors]
    );

    // Carry the Business Goals fallback into per-target details so upload
    // metadata still sends `rejection_label_name`. An effect (not just initial
    // state) because when resuming mid-flow the form is hydrated from the
    // usecase asynchronously. Seeds only when the step-1 value actually
    // changed since the last seed (tracked across remounts via the cache). A
    // target whose value went blank (e.g. its details were reset) is re-seeded
    // too. The select itself lives on Business Goals, not this step.
    const fallbackFromGoals = form.fallbackLabel ?? null;
    const lastSeededFallback = useRef(cached ? cached.seededFallback : undefined);
    useEffect(() => {
        const stepValueChanged = lastSeededFallback.current !== fallbackFromGoals;
        if (stepValueChanged) lastSeededFallback.current = fallbackFromGoals;
        setDetailsByTarget((prev) => {
            const seed = (details) => {
                if (stepValueChanged || (details.rejectionLabel == null && fallbackFromGoals != null)) {
                    return { ...details, rejectionLabel: fallbackFromGoals };
                }
                return details;
            };
            const dataset = seed(prev.dataset);
            const benchmark = seed(prev.benchmark);
            if (dataset === prev.dataset && benchmark === prev.benchmark) return prev;
            return { dataset, benchmark };
        });
        setDetailsByFileId((prev) => {
            if (!Object.keys(prev).length) return prev;
            let changed = false;
            const next = { ...prev };
            Object.keys(next).forEach((id) => {
                const details = next[id];
                if (
                    stepValueChanged ||
                    (details.rejectionLabel == null && fallbackFromGoals != null)
                ) {
                    next[id] = { ...details, rejectionLabel: fallbackFromGoals };
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [fallbackFromGoals, detailsByTarget]);

    // Seed empty-response default when multilabel mode changes. Do not overwrite
    // an explicit user choice that is still valid for the current mode.
    useEffect(() => {
        const nextDefault = emptyResponseDefault(isMultiLabel);
        const patchTarget = (details) => {
            const current = details.emptyMultilabelHandling;
            if (isMultiLabel) {
                if (current == null) return { ...details, emptyMultilabelHandling: nextDefault };
                return details;
            }
            // Single-label / NER / generative: drop invalid none_selected.
            if (current == null || current === 'none_selected') {
                return { ...details, emptyMultilabelHandling: nextDefault };
            }
            return details;
        };
        setDetailsByTarget((prev) => ({
            dataset: patchTarget(prev.dataset),
            benchmark: patchTarget(prev.benchmark),
        }));
        setDetailsByFileId((prev) => {
            if (!Object.keys(prev).length) return prev;
            let changed = false;
            const next = { ...prev };
            Object.keys(next).forEach((id) => {
                const patched = patchTarget(next[id]);
                if (patched !== next[id]) {
                    next[id] = patched;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [isMultiLabel]);

    // Copy remaining per-file details back onto the target when leaving split
    // mode so going 2→1 files does not drop work.
    const wasSplitRef = useRef(splitFileDetails);
    useEffect(() => {
        const wasSplit = wasSplitRef.current;
        wasSplitRef.current = splitFileDetails;
        if (!wasSplit || splitFileDetails) return;
        setDetailsByTarget((prev) => {
            const next = { ...prev };
            let changed = false;
            ['dataset', 'benchmark'].forEach((target) => {
                const file = slots[target]?.files?.[0];
                const fileDetails = file?.id ? detailsByFileIdRef.current[file.id] : null;
                if (fileDetails) {
                    next[target] = fileDetails;
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [splitFileDetails, slots]);

    // Seed per-file details when splitting; prune entries whose files were removed.
    useEffect(() => {
        setDetailsByFileId((prev) => {
            const liveIds = new Set();
            ['dataset', 'benchmark'].forEach((target) => {
                (slots[target]?.files || []).forEach((entry) => {
                    if (entry?.id) liveIds.add(entry.id);
                });
            });
            let changed = false;
            const next = { ...prev };
            if (splitFileDetails) {
                ['dataset', 'benchmark'].forEach((target) => {
                    const files = slots[target]?.files || [];
                    const targetDetails = detailsByTargetRef.current[target] || emptyDetails();
                    files.forEach((entry, index) => {
                        if (!entry?.id) return;
                        if (next[entry.id]) return;
                        next[entry.id] =
                            index === 0 && isDetailsConfigured(targetDetails)
                                ? { ...targetDetails }
                                : {
                                      ...emptyDetails(),
                                      rejectionLabel: targetDetails.rejectionLabel ?? null,
                                      emptyMultilabelHandling:
                                          targetDetails.emptyMultilabelHandling ?? null,
                                  };
                        changed = true;
                    });
                });
            }
            Object.keys(next).forEach((id) => {
                if (!liveIds.has(id)) {
                    delete next[id];
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [slots, splitFileDetails]);

    useEffect(() => {
        if (!hideShareDetailsToggle) return;
        setShareDetails(false);
        setShareDetailsError(null);
    }, [hideShareDetailsToggle]);

    /** Primary file entry whose columns drive a details form (shared mode → dataset first). */
    const slotForTarget = useCallback(
        (target) => {
            if (!shareDetails || !showDatasetCard || !showBenchmarkCard) {
                return primaryFileEntry(slots[target]);
            }
            // Prefer dataset when it still has local or post-upload metadata.
            return slotHasLocalData(slots.dataset)
                ? primaryFileEntry(slots.dataset)
                : primaryFileEntry(slots.benchmark);
        },
        [shareDetails, showDatasetCard, showBenchmarkCard, slots]
    );

    const filesForDetailsTarget = useCallback(
        (target) => {
            if (shareDetails && showDatasetCard && showBenchmarkCard) {
                return slotHasLocalData(slots.dataset)
                    ? slots.dataset?.files
                    : slots.benchmark?.files;
            }
            return slots[target]?.files;
        },
        [shareDetails, showDatasetCard, showBenchmarkCard, slots]
    );

    // Shared-details mode with files on both sides: the one visible form is
    // submitted for BOTH targets, so its selectable columns (and clamping)
    // must come from every uploaded file across the two slots — not just the
    // primary side's files.
    const sharedDetailsSpansBothTargets =
        shareDetails &&
        !splitFileDetails &&
        showDatasetCard &&
        showBenchmarkCard &&
        (slots.dataset?.files || []).length > 0 &&
        (slots.benchmark?.files || []).length > 0;

    /** Column source for a details form: cross-target when details are shared. */
    const columnsFilesForTarget = useCallback(
        (target) =>
            sharedDetailsSpansBothTargets
                ? [...(slots.dataset?.files || []), ...(slots.benchmark?.files || [])]
                : slots[target]?.files || [],
        [sharedDetailsSpansBothTargets, slots]
    );

    /** Remove one pending local pick by entry id. */
    const handleRemovePending = useCallback(
        (target, entryId) => {
            upload.removeFile(target, entryId);
            // If this was the last pending entry and nothing remains uploaded,
            // clear details so the form doesn't stick around empty.
            const remaining = (slots[target]?.files || []).filter((e) => e.id !== entryId);
            const stillHasUploaded =
                target === 'benchmark'
                    ? (usecase?.benchmarks || []).length > 0
                    : (usecase?.datasets || []).length > 0;
            if (!remaining.length && !stillHasUploaded) {
                // Keep the task-level fallback label seeded from Business
                // Goals — removing a file must not blank it.
                patchDetails(target, {
                    ...emptyDetails(),
                    rejectionLabel: detailsByTarget[target]?.rejectionLabel ?? null,
                });
            }
        },
        [upload, slots, usecase, patchDetails, detailsByTarget]
    );

    /**
     * Detach an already-uploaded dataset/benchmark from the usecase (shown
     * after Next → Back when chips mirror usecase.datasets / benchmarks).
     * Resolves user-facing / raw names to every matching attached name,
     * including per-subtask ``__t_<6chars>`` clones.
     *
     * In the create-task wizard the task-specific copy belongs only to the
     * draft being built, so it is removed immediately; the "Remove from this
     * task?" confirmation modal is reserved for managing an existing task
     * (the task-page Upload Data modal / Data Overview).
     */
    const handleRemoveUploaded = useCallback(
        (target, uploadedName) => {
            const isBenchmark = target === 'benchmark';
            const attached = isBenchmark
                ? usecase?.benchmarks || []
                : usecase?.datasets || [];
            const names = uploadedName
                ? attached.filter((name) => {
                      if (name === uploadedName) return true;
                      const stripped = stripCloneSuffix(name);
                      const uploadedStripped = stripCloneSuffix(uploadedName);
                      return (
                          stripped === uploadedName ||
                          name === uploadedStripped ||
                          stripped === uploadedStripped
                      );
                  })
                : attached;
            if (!names.length) {
                upload.removeFile(target);
                return;
            }
            const performRemoval = async () => {
                await removeFromUsecase({
                    usecase,
                    namesToRemove: names,
                    isBenchmark,
                });
                const nameSet = new Set(names);
                const displayKeys = new Set(
                    names.map((n) => stripCloneSuffix(n) || n)
                );
                const remainingLocal = (slots[target]?.files || []).filter((entry) => {
                    if (nameSet.has(entry.uploadName)) return false;
                    const display = stripCloneSuffix(entry.uploadName || '') || entry.uploadName;
                    return !displayKeys.has(display);
                });
                // Drop matching post-upload metadata chips; keep unrelated
                // pending picks.
                (slots[target]?.files || []).forEach((entry) => {
                    const display =
                        stripCloneSuffix(entry.uploadName || '') || entry.uploadName;
                    if (
                        entry.id &&
                        (nameSet.has(entry.uploadName) || displayKeys.has(display))
                    ) {
                        upload.removeFile(target, entry.id);
                    }
                });
                const remainingUploaded = attached.filter((n) => !nameSet.has(n));
                if (!remainingLocal.length && !remainingUploaded.length) {
                    // Keep the task-level fallback label — see handleRemovePending.
                    patchDetails(target, {
                        ...emptyDetails(),
                        rejectionLabel: detailsByTarget[target]?.rejectionLabel ?? null,
                    });
                }
            };
            if (!isModal) {
                performRemoval().then(() => refreshUsecases());
                return;
            }
            confirmRemoval({
                names,
                isBenchmark,
                onConfirm: performRemoval,
            });
        },
        [
            slots,
            upload,
            usecase,
            confirmRemoval,
            removeFromUsecase,
            patchDetails,
            detailsByTarget,
            isModal,
            refreshUsecases,
        ]
    );

    const defaultedFor = useRef(cached?.defaultedFor ?? {});
    const extractedFor = useRef(cached?.extractedFor ?? {});

    const extractLabels = useCallback(
        async (target, column, entries, delimiter = null, fileId = null) => {
            const readyEntries = (
                Array.isArray(entries) ? entries : entries ? [entries] : []
            ).filter(
                (entry) =>
                    entry?.file ||
                    entry?.content ||
                    entry?.datasetName ||
                    entry?.librarySourceName ||
                    entry?.huggingFaceDatasetId
            );
            if (!column || !readyEntries.length) return;
            const usecaseName = usecase?.usecase_name;
            const knownAiLabels = knownLabelsForAiGeneration({
                formLabels: form.labels,
                usecase,
                taskType,
            });
            const matchTaskLabel = buildTaskLabelMatcher(form.labels);
            const applyExtracted = (labels) => {
                if (fileId) {
                    setDetailsByFileId((prev) => ({
                        ...prev,
                        [fileId]: applyExtractedLabels(
                            prev[fileId] || emptyDetails(),
                            labels,
                            matchTaskLabel
                        ),
                    }));
                    return;
                }
                setDetailsByTarget((prev) => ({
                    ...prev,
                    [target]: applyExtractedLabels(prev[target], labels, matchTaskLabel),
                }));
            };
            const scannable = readyEntries.filter(
                (entry) => !isAiGeneratedUploadEntry(entry, usecaseName)
            );
            const hasAi = scannable.length !== readyEntries.length;
            // Generate-with-AI has no file bytes yet; the labels are the
            // Business Goals / generation targets, not a distinct-values scan.
            if (!scannable.length) {
                if (knownAiLabels.length) applyExtracted(knownAiLabels);
                return;
            }
            if (fileId) {
                patchFileDetails(fileId, { labelsLoading: true });
            } else {
                patchDetails(target, { labelsLoading: true });
            }
            const distinctTarget =
                taskType === TaskType.NER ? 'ner_entities' : 'classification';
            try {
                const perFileLabels = await Promise.all(
                    scannable.map(async (entry) => {
                        try {
                            if (entry.file) {
                                const response = await fetchFileLabelsWithUpload({
                                    file: entry.file,
                                    filename: entry.file.name,
                                    clientId: usecase?.client_id,
                                    labelColumn: column,
                                    fileType: entry.format,
                                    taskType,
                                    delimiter,
                                });
                                return response?.labels || [];
                            }
                            if (entry.huggingFaceDatasetId) {
                                return await fetchHfDatasetLabels({
                                    datasetId: entry.huggingFaceDatasetId,
                                    labelColumn: column,
                                    clientId: usecase?.client_id,
                                    isMultilabel: isMultiLabel,
                                    delimiter,
                                });
                            }
                            if (entry.content && entry.format) {
                                const response = await fetchFileLabelsFromContent({
                                    filename: entry.uploadName || 'dataset',
                                    clientId: usecase?.client_id,
                                    labelColumn: column,
                                    fileContent: entry.content,
                                    fileType: entry.format,
                                    taskType,
                                    delimiter,
                                });
                                return response?.labels || [];
                            }
                            const datasetName = entry.datasetName || entry.librarySourceName;
                            if (!datasetName || !usecase?.client_id) return [];
                            const sample = await fetchLibraryDatasetSample({
                                clientId: usecase.client_id,
                                datasetName,
                                isBenchmark: target === 'benchmark',
                                labelColumn: column,
                                distinctTarget,
                                limit: 1,
                            });
                            return sample.distinct_labels || [];
                        } catch (error) {
                            console.error(
                                `Failed to extract labels from ${entry.file?.name || entry.uploadName}:`,
                                error
                            );
                            return [];
                        }
                    })
                );
                applyExtracted(
                    unionLabels([...(hasAi ? [knownAiLabels] : []), ...perFileLabels])
                );
            } catch (error) {
                sparkToast.error(error?.message || 'Failed to extract labels from file');
                if (fileId) {
                    patchFileDetails(fileId, { labelsLoading: false });
                } else {
                    patchDetails(target, { labelsLoading: false });
                }
            }
        },
        [patchDetails, patchFileDetails, taskType, usecase, form.labels, isMultiLabel]
    );

    const handleLabelColumnChange = useCallback(
        (target, fileId = null) => (column) => {
            if (fileId) {
                extractedFor.current[fileId] = null;
                delimiterDetectedFor.current[fileId] = null;
                patchFileDetails(fileId, {
                    labelColumn: column,
                    extractedLabels: [],
                    labelMappings: {},
                    labelsDelimiter: null,
                });
                return;
            }
            // Force the multi-file extraction effect to re-run for this target.
            extractedFor.current[target] = null;
            delimiterDetectedFor.current[target] = null;
            patchDetails(target, {
                labelColumn: column,
                extractedLabels: [],
                labelMappings: {},
                // Re-detect the multilabel delimiter for the new column.
                labelsDelimiter: null,
            });
        },
        [patchDetails, patchFileDetails]
    );

    // In shared-details mode the one visible form is backed by the *dataset*
    // details, but its columns come from the benchmark's primary file when
    // the dataset slot is empty (see slotForTarget). Defaults and label
    // extraction must follow the same fallback, otherwise a benchmark-only
    // upload leaves the shared form without a default label column, without
    // extracted labels, and without the empty-response-handling section.
    const sharedDetailsFallsBackToBenchmark =
        shareDetails &&
        showDatasetCard &&
        showBenchmarkCard &&
        !slotHasLocalData(slots.dataset);

    // Drop files that share no columns with the kept prefix; toast (and
    // wizard card error) for the rejected ones. A same-batch pick with no
    // shared columns at all is aborted instead of keeping one leftover file.
    // Per-file details (split mode) allow different schemas, so skip this.
    useEffect(() => {
        if (splitFileDetails) return;
        ['dataset', 'benchmark'].forEach((target) => {
            const files = slots[target]?.files || [];
            if (files.length < 2) return;
            if (files.some((entry) => entry.columnsLoading)) return;
            const { rejected } = partitionEntriesByColumnOverlap(files);
            const batchId = files[0]?.overlapBatchId;
            const sameBatch =
                Boolean(batchId) && files.every((entry) => entry.overlapBatchId === batchId);
            const abortBatch =
                sameBatch && shouldAbortUploadForNoSharedColumns(files);
            if (!rejected.length && !abortBatch) return;
            const toRemove = abortBatch ? files : rejected;
            const fresh = toRemove.filter((entry) => !overlapRejectRef.current.has(entry.id));
            toRemove.forEach((entry) => overlapRejectRef.current.add(entry.id));
            upload.removeFiles(
                target,
                toRemove.map((entry) => entry.id)
            );
            if (!fresh.length) return;
            showNoSharedColumnsToast(abortBatch ? rejected : fresh);
            if (abortBatch && isModal && hideUploadSection) {
                onDismiss?.();
                return;
            }
            if (!isModal) {
                setColumnErrorByTarget((prev) => ({
                    ...prev,
                    [target]: SHARED_COLUMNS_CARD_ERROR,
                }));
            }
        });
    }, [slots, isModal, hideUploadSection, onDismiss, upload.removeFiles, splitFileDetails]);

    // When the primary file's columns arrive, default the example/label columns.
    // Label extraction for classification/NER is handled by the effect below so
    // it unions labels across every pending file for the target.
    useEffect(() => {
        const defaultPatchForEntry = (entry, currentDetails, filesForDelimiter) => {
            const columns = entry.columns || [];
            const exampleColumn =
                findColumnFuzzy(columns, ['example']) ??
                (columns.includes('text') ? 'text' : null);
            const labelColumn = defaultLabelColumn(taskType, columns);
            const hasLabels = currentDetails?.hasLabels !== false;
            const looksAi = isAiGeneratedDataset({
                datasetName: entry.datasetName || entry.uploadName,
                usecaseName: usecase?.usecase_name,
            });
            const detectedDelimiter =
                isMultiLabel &&
                hasLabels &&
                labelColumn &&
                !currentDetails?.labelsDelimiter
                    ? looksAi
                        ? 'list'
                        : guessLabelsDelimiterFromEntries(filesForDelimiter, labelColumn)
                    : null;
            return {
                ...(exampleColumn ? { exampleColumn } : {}),
                ...(hasLabels && labelColumn !== undefined ? { labelColumn } : {}),
                ...(looksAi && !currentDetails?.dataCreation
                    ? { dataCreation: LLM_CREATED_DATA_CREATION }
                    : {}),
                ...(looksAi && currentDetails?.hasLabels === false ? { hasLabels: true } : {}),
                ...(detectedDelimiter ? { labelsDelimiter: detectedDelimiter } : {}),
                // Agentic tasks answer "Does your data contains trajectories?"
                // Yes by default, with a `messages` column and its keys
                // pre-picked from the sample rows (Figma 4012:19043). Only
                // for files still to be uploaded — a dataset hydrated from
                // the server keeps its stored answer — and a Yes the user
                // already configured is left alone.
                ...(isAgentic && isPendingUploadEntry(entry) && !currentDetails?.hasTrajectories
                    ? trajectoryYesDetails(
                          sampleRecordsFromUploadEntry(entry, TRAJECTORY_SAMPLE_ROWS),
                          columns
                      )
                    : {}),
            };
        };

        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((target) => {
                (slots[target]?.files || []).forEach((entry) => {
                    if (!entry.columns?.length) return;
                    const columns = entry.columns;
                    const key = `file:${entry.id}:${columns.join(',')}`;
                    if (defaultedFor.current[key]) return;
                    const existing = detailsByFileId[entry.id];
                    if (existing?.exampleColumn || existing?.labelColumn) {
                        defaultedFor.current[key] = true;
                        return;
                    }
                    defaultedFor.current[key] = true;
                    const currentDetails = existing || detailsByTarget[target];
                    const patch = defaultPatchForEntry(entry, currentDetails, [entry]);
                    if (Object.keys(patch).length) patchFileDetails(entry.id, patch);
                });
            });
            return;
        }

        ['dataset', 'benchmark'].forEach((target) => {
            let entry = primaryFileEntry(slots[target]);
            if (target === 'dataset' && !entry.file && sharedDetailsFallsBackToBenchmark) {
                entry = primaryFileEntry(slots.benchmark);
            }
            if (!entry.columns.length) return;
            const files =
                target === 'dataset' && sharedDetailsFallsBackToBenchmark
                    ? slots.benchmark?.files
                    : slots[target]?.files;
            const overlap = overlapForFiles(files);
            if (overlap.status === COLUMN_OVERLAP.unknown) return;
            const columns =
                overlap.status === COLUMN_OVERLAP.partial ||
                overlap.status === COLUMN_OVERLAP.identical
                    ? overlap.shared
                    : entry.columns;
            if (!columns.length) return;
            // Key by the pick's entry id (stable across Back/Next via the
            // cached slots, new on every pick) — keying by uploadName left a
            // stale "already defaulted" mark after remove + re-pick of the
            // same file, so the example/labels columns never re-defaulted.
            const key = `${target}:${entry.id || entry.uploadName}:${columns.join(',')}`;
            if (defaultedFor.current[key]) return;
            defaultedFor.current[key] = true;

            const filesForDelimiter =
                target === 'dataset' && sharedDetailsFallsBackToBenchmark
                    ? slots.benchmark?.files
                    : slots[target]?.files;
            patchDetails(
                target,
                defaultPatchForEntry(
                    { ...entry, columns },
                    detailsByTarget[target],
                    filesForDelimiter
                )
            );
        });
    }, [
        slots,
        taskType,
        patchDetails,
        patchFileDetails,
        sharedDetailsFallsBackToBenchmark,
        detailsByTarget,
        detailsByFileId,
        usecase?.usecase_name,
        isMultiLabel,
        isAgentic,
        splitFileDetails,
    ]);

    // Shared-column dropdowns: drop example/label picks that are not in the
    // intersection, then re-default from the shared list. In shared-details
    // mode the intersection spans every file across BOTH targets, since the
    // one visible form is submitted for the dataset and the benchmark alike.
    useEffect(() => {
        if (splitFileDetails) return;
        ['dataset', 'benchmark'].forEach((target) => {
            const overlap = overlapForFiles(columnsFilesForTarget(target));
            if (
                overlap.status !== COLUMN_OVERLAP.partial &&
                overlap.status !== COLUMN_OVERLAP.identical
            ) {
                return;
            }
            const allowed = new Set(overlap.shared);
            const details = detailsByTarget[target];
            if (!details) return;
            const patch = {};
            if (details.exampleColumn && !allowed.has(details.exampleColumn)) {
                patch.exampleColumn = findColumnFuzzy(overlap.shared, ['example']) ?? null;
            }
            if (details.labelColumn && !allowed.has(details.labelColumn)) {
                const nextLabel = defaultLabelColumn(taskType, overlap.shared);
                patch.labelColumn = nextLabel !== undefined ? nextLabel : undefined;
            }
            if (Object.keys(patch).length) patchDetails(target, patch);
        });
    }, [
        slots,
        detailsByTarget,
        taskType,
        patchDetails,
        splitFileDetails,
        columnsFilesForTarget,
    ]);

    // Auto-detect the multilabel labels delimiter from the first records of
    // the selected label column (raw JSON/Python list cells → 'list'; otherwise
    // the first separator found among ; | \t ,). Uses raw cell values so
    // preview-unwrapping cannot turn lists into comma-joined strings. Runs
    // once per column/file set; an undetectable structure leaves the select
    // empty for the user.
    const delimiterDetectedFor = useRef(cached?.delimiterDetectedFor ?? {});
    const languagesDetectedFor = useRef(cached?.languagesDetectedFor ?? null);
    useEffect(() => {
        if (!isMultiLabel) return;
        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((target) => {
                (slots[target]?.files || []).forEach((entry) => {
                    const details = detailsByFileId[entry.id];
                    const column = details?.labelColumn;
                    if (!column || details.labelsDelimiter) return;
                    if (!uploadEntryHasPreviewSamples(entry)) return;
                    const signature = `${column}::${entry.id}`;
                    if (delimiterDetectedFor.current[entry.id] === signature) return;

                    const detected = guessLabelsDelimiterFromEntries([entry], column);
                    if (detected) {
                        patchFileDetails(entry.id, { labelsDelimiter: detected });
                    } else if (
                        fieldValuesFromUploadEntry(entry, column, 20, { raw: true }).length
                    ) {
                        delimiterDetectedFor.current[entry.id] = signature;
                    }
                });
            });
            return;
        }
        ['dataset', 'benchmark'].forEach((target) => {
            const details = detailsByTarget[target];
            const column = details?.labelColumn;
            if (!column || details.labelsDelimiter) return;
            let entries = slots[target]?.files || [];
            if (target === 'dataset' && !entries.length && sharedDetailsFallsBackToBenchmark) {
                entries = slots.benchmark?.files || [];
            }
            const sampleable = entries.filter(uploadEntryHasPreviewSamples);
            if (!sampleable.length) return;

            const signature = `${column}::${sampleable.map((entry) => entry.id).join('|')}`;
            if (delimiterDetectedFor.current[target] === signature) return;

            const detected = guessLabelsDelimiterFromEntries(sampleable, column);
            if (detected) {
                patchDetails(target, { labelsDelimiter: detected });
            } else if (
                sampleable.some(
                    (entry) => fieldValuesFromUploadEntry(entry, column, 20, { raw: true }).length
                )
            ) {
                // Only remember misses when we had cells to sniff, so a later
                // sample load can still re-detect.
                delimiterDetectedFor.current[target] = signature;
            }
        });
    }, [
        slots,
        detailsByTarget,
        detailsByFileId,
        isMultiLabel,
        sharedDetailsFallsBackToBenchmark,
        patchDetails,
        patchFileDetails,
        splitFileDetails,
    ]);

    // Column discovery for a pending pick (Hugging Face / library) does not
    // reset details. Drop leftover extracted rows so they cannot sit under a
    // still-loading "Which column contain the labels?" select.
    useEffect(() => {
        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((target) => {
                (slots[target]?.files || []).forEach((entry) => {
                    if (!entry.columnsLoading) return;
                    extractedFor.current[entry.id] = null;
                    const current = detailsByFileId[entry.id];
                    if (!current?.extractedLabels?.length && !current?.labelsLoading) return;
                    patchFileDetails(entry.id, {
                        extractedLabels: [],
                        labelMappings: {},
                        labelsLoading: false,
                    });
                });
            });
            return;
        }
        ['dataset', 'benchmark'].forEach((target) => {
            const files = filesForDetailsTarget(target) || [];
            if (!files.some((entry) => entry.columnsLoading)) return;
            extractedFor.current[target] = null;
            const current = detailsByTarget[target];
            if (!current?.extractedLabels?.length && !current?.labelsLoading) return;
            patchDetails(target, {
                extractedLabels: [],
                labelMappings: {},
                labelsLoading: false,
            });
        });
    }, [
        filesForDetailsTarget,
        detailsByTarget,
        detailsByFileId,
        patchDetails,
        patchFileDetails,
        slots,
        splitFileDetails,
    ]);

    // Extract (and union) labels from every pending file once the label column
    // is set and files are ready. Re-runs when files are added/removed.
    // Multilabel file bytes wait for the labels delimiter (auto-detected or
    // user-picked) so cells split correctly. Library / attached datasets use
    // Mongo distincts and can extract before that sniff completes.
    useEffect(() => {
        if (!(isClassification || isNer)) return;
        const isReadyEntry = (entry) =>
            Boolean(
                entry.file ||
                    entry.content ||
                    entry.datasetName ||
                    entry.librarySourceName ||
                    entry.huggingFaceDatasetId
            );
        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((target) => {
                (slots[target]?.files || []).forEach((entry) => {
                    const details = detailsByFileId[entry.id];
                    if (!details || details.hasLabels === false) return;
                    const column = details.labelColumn;
                    if (!column) return;
                    const delimiter = isMultiLabel ? details.labelsDelimiter || null : null;
                    if (!isReadyEntry(entry)) return;
                    if (isMultiLabel && !delimiter) {
                        const needsFileParse = Boolean(
                            entry.file || (entry.content && entry.format)
                        );
                        if (needsFileParse) return;
                    }
                    if (entry.columnsLoading) return;
                    const signature = `${column}::${delimiter || ''}::${entry.id}`;
                    if (extractedFor.current[entry.id] === signature) return;
                    extractedFor.current[entry.id] = signature;
                    extractLabels(target, column, [entry], delimiter, entry.id);
                });
            });
            return;
        }
        ['dataset', 'benchmark'].forEach((target) => {
            if (detailsByTarget[target]?.hasLabels === false) return;
            const column = detailsByTarget[target]?.labelColumn;
            if (!column) return;
            const delimiter = isMultiLabel
                ? detailsByTarget[target]?.labelsDelimiter || null
                : null;
            let entries = (slots[target]?.files || []).filter(isReadyEntry);
            if (target === 'dataset' && !entries.length && sharedDetailsFallsBackToBenchmark) {
                entries = (slots.benchmark?.files || []).filter(isReadyEntry);
            }
            if (!entries.length) return;
            // File bytes need the delimiter to split cells. Library / attached
            // datasets use Mongo distincts, so they can extract before sniffing.
            if (isMultiLabel && !delimiter) {
                const needsFileParse = entries.some(
                    (entry) => entry.file || (entry.content && entry.format)
                );
                if (needsFileParse) return;
            }
            if (entries.some((entry) => entry.columnsLoading)) return;

            const signature = `${column}::${delimiter || ''}::${entries
                .map((entry) => entry.id)
                .join('|')}`;
            if (extractedFor.current[target] === signature) return;
            extractedFor.current[target] = signature;
            extractLabels(target, column, entries, delimiter);
        });
    }, [
        slots,
        detailsByTarget,
        detailsByFileId,
        isClassification,
        isNer,
        isMultiLabel,
        extractLabels,
        sharedDetailsFallsBackToBenchmark,
        splitFileDetails,
    ]);

    // "How should empty … values be handled?" is only relevant when empty
    // response values can actually occur. Scan every picked file client-side:
    // when all of a target's files were fully scanned (content holds the whole
    // file) and none has an empty value in the response column, hide the
    // section. Library and Hugging Face picks have no file bytes — the backend
    // answers instead. Hide until that answer arrives so the radios do not
    // flash on then off. Unverifiable files (large/truncated, parquet, failed
    // remote scan) keep it visible. Per-entry results are cached so re-renders
    // don't re-parse file content.
    const emptyScanCacheRef = useRef({});
    // Backend answers for library picks, keyed `${entryId}::${column}` so
    // switching the labels column triggers a fresh query.
    const [libraryEmptyScans, setLibraryEmptyScans] = useState({});
    const libraryEmptyScanPendingRef = useRef(new Set());
    const recordLibraryEmptyScan = useCallback(
        (entryId, column, hasEmpty, valueKind = EMPTY_VALUE_KIND_RESPONSE) => {
            if (!entryId || !column) return;
            setLibraryEmptyScans((prev) => ({
                ...prev,
                [emptyScanKey(entryId, column, valueKind)]: hasEmpty,
            }));
        },
        []
    );

    // Shared details apply to both targets' files, so a benchmark file with
    // empty values must keep the shared section visible.
    const sharedDetailsMode = shareDetails && showDatasetCard && showBenchmarkCard;

    const showEmptyResponsesByTarget = useMemo(() => {
        const cache = emptyScanCacheRef.current;
        const compute = (target) => {
            const column = detailsByTarget[target]?.labelColumn;
            const entries =
                target === 'dataset' && sharedDetailsMode
                    ? [...(slots.dataset?.files || []), ...(slots.benchmark?.files || [])]
                    : slots[target]?.files || [];
            return shouldShowEmptyResponsesSection({
                entries,
                column,
                fileCache: cache,
                libraryEmptyScans,
                columnsLoading: entries.some((entry) => entry?.columnsLoading),
                skipEntry: (entry) => isAiGeneratedUploadEntry(entry, usecase?.usecase_name),
            });
        };
        return { dataset: compute('dataset'), benchmark: compute('benchmark') };
    }, [slots, detailsByTarget, sharedDetailsMode, libraryEmptyScans, usecase?.usecase_name]);

    const showEmptyResponsesByFileId = useMemo(() => {
        const cache = emptyScanCacheRef.current;
        const result = {};
        ['dataset', 'benchmark'].forEach((target) => {
            (slots[target]?.files || []).forEach((entry) => {
                if (!entry?.id) return;
                const column = detailsByFileId[entry.id]?.labelColumn;
                result[entry.id] = shouldShowEmptyResponsesSection({
                    entries: [entry],
                    column,
                    fileCache: cache,
                    libraryEmptyScans,
                    columnsLoading: Boolean(entry.columnsLoading),
                    skipEntry: (item) => isAiGeneratedUploadEntry(item, usecase?.usecase_name),
                });
            });
        });
        return result;
    }, [slots, detailsByFileId, libraryEmptyScans, usecase?.usecase_name]);

    // Agentic Yes branch: same visibility rule for the mapped trajectory
    // column (empty cells found, or the file could not be verified).
    const trajectoryScanColumn = (d) => (d?.hasTrajectories ? d.trajectoryColumn : null) || null;

    const showEmptyTrajectoriesByTarget = useMemo(() => {
        if (!isAgentic) return { dataset: false, benchmark: false };
        const cache = emptyScanCacheRef.current;
        const compute = (target) => {
            const column = trajectoryScanColumn(detailsByTarget[target]);
            const entries =
                target === 'dataset' && sharedDetailsMode
                    ? [...(slots.dataset?.files || []), ...(slots.benchmark?.files || [])]
                    : slots[target]?.files || [];
            return shouldShowEmptyTrajectoriesSection({
                entries,
                column,
                fileCache: cache,
                libraryEmptyScans,
                columnsLoading: entries.some((entry) => entry?.columnsLoading),
                skipEntry: (entry) => isAiGeneratedUploadEntry(entry, usecase?.usecase_name),
            });
        };
        return { dataset: compute('dataset'), benchmark: compute('benchmark') };
    }, [isAgentic, slots, detailsByTarget, sharedDetailsMode, libraryEmptyScans, usecase?.usecase_name]);

    const showEmptyTrajectoriesByFileId = useMemo(() => {
        const result = {};
        if (!isAgentic) return result;
        const cache = emptyScanCacheRef.current;
        ['dataset', 'benchmark'].forEach((target) => {
            (slots[target]?.files || []).forEach((entry) => {
                if (!entry?.id) return;
                result[entry.id] = shouldShowEmptyTrajectoriesSection({
                    entries: [entry],
                    column: trajectoryScanColumn(detailsByFileId[entry.id]),
                    fileCache: cache,
                    libraryEmptyScans,
                    columnsLoading: Boolean(entry.columnsLoading),
                    skipEntry: (item) => isAiGeneratedUploadEntry(item, usecase?.usecase_name),
                });
            });
        });
        return result;
    }, [isAgentic, slots, detailsByFileId, libraryEmptyScans, usecase?.usecase_name]);

    // Library / Hugging Face picks have no bytes to scan for empty
    // trajectory cells: library datasets are answered by the sample endpoint
    // (`has_empty_trajectory_values`); Hugging Face picks are recorded as
    // unverifiable (null) so the section stays visible.
    useEffect(() => {
        if (!isAgentic) return;
        const scanRemoteTrajectoryEntry = (entry, column, slotTarget) => {
            if (!column || isAiGeneratedUploadEntry(entry, usecase?.usecase_name)) return;
            const key = emptyScanKey(entry.id, column, EMPTY_VALUE_KIND_TRAJECTORY);
            if (key in libraryEmptyScans || libraryEmptyScanPendingRef.current.has(key)) {
                return;
            }
            if (entry.fromLibrary && entry.librarySourceName) {
                libraryEmptyScanPendingRef.current.add(key);
                fetchLibraryDatasetHasEmptyTrajectories({
                    clientId: usecase?.client_id,
                    datasetName: entry.librarySourceName,
                    isBenchmark: slotTarget === 'benchmark',
                    trajectoryColumn: column,
                })
                    .catch(() => null)
                    .then((hasEmpty) => {
                        libraryEmptyScanPendingRef.current.delete(key);
                        recordLibraryEmptyScan(
                            entry.id,
                            column,
                            hasEmpty ?? null,
                            EMPTY_VALUE_KIND_TRAJECTORY
                        );
                    });
                return;
            }
            if (entry.fromHuggingFace && entry.huggingFaceDatasetId) {
                recordLibraryEmptyScan(entry.id, column, null, EMPTY_VALUE_KIND_TRAJECTORY);
            }
        };

        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((slotTarget) => {
                (slots[slotTarget]?.files || []).forEach((entry) => {
                    scanRemoteTrajectoryEntry(
                        entry,
                        trajectoryScanColumn(detailsByFileId[entry.id]),
                        slotTarget
                    );
                });
            });
            return;
        }
        [
            {
                column: trajectoryScanColumn(detailsByTarget.dataset),
                slotTargets: sharedDetailsMode ? ['dataset', 'benchmark'] : ['dataset'],
            },
            { column: trajectoryScanColumn(detailsByTarget.benchmark), slotTargets: ['benchmark'] },
        ].forEach(({ column, slotTargets }) => {
            if (!column) return;
            slotTargets.forEach((slotTarget) => {
                (slots[slotTarget]?.files || []).forEach((entry) => {
                    scanRemoteTrajectoryEntry(entry, column, slotTarget);
                });
            });
        });
    }, [
        isAgentic,
        slots,
        detailsByTarget,
        detailsByFileId,
        splitFileDetails,
        sharedDetailsMode,
        libraryEmptyScans,
        usecase?.client_id,
        usecase?.usecase_name,
        recordLibraryEmptyScan,
    ]);

    // Ask the backend for any (library entry, labels column) pair the
    // visibility computation needs but has no answer for yet — the initial
    // pick seeds the stored column, so this fires mainly when the user
    // switches the labels column afterwards.
    useEffect(() => {
        const scanRemoteEntry = (entry, column, slotTarget, delimiter) => {
            if (!column || isAiGeneratedUploadEntry(entry, usecase?.usecase_name)) return;
            const key = `${entry.id}::${column}`;
            if (key in libraryEmptyScans || libraryEmptyScanPendingRef.current.has(key)) {
                return;
            }
            if (entry.fromLibrary && entry.librarySourceName) {
                libraryEmptyScanPendingRef.current.add(key);
                fetchLibraryDatasetHasEmptyLabels({
                    clientId: usecase?.client_id,
                    datasetName: entry.librarySourceName,
                    isBenchmark: slotTarget === 'benchmark',
                    labelColumn: column,
                })
                    .catch(() => null)
                    .then((hasEmpty) => {
                        libraryEmptyScanPendingRef.current.delete(key);
                        recordLibraryEmptyScan(entry.id, column, hasEmpty ?? null);
                    });
                return;
            }
            if (entry.fromHuggingFace && entry.huggingFaceDatasetId) {
                libraryEmptyScanPendingRef.current.add(key);
                fetchHfDatasetEmptyValues({
                    datasetId: entry.huggingFaceDatasetId,
                    column,
                    clientId: usecase?.client_id,
                    isMultilabel: isMultiLabel,
                    delimiter,
                    splits: [
                        entry.huggingFaceTrainSplit,
                        entry.huggingFaceTestSplit,
                    ].filter(Boolean),
                })
                    .catch(() => null)
                    .then((hasEmpty) => {
                        libraryEmptyScanPendingRef.current.delete(key);
                        recordLibraryEmptyScan(entry.id, column, hasEmpty ?? null);
                    });
            }
        };

        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((slotTarget) => {
                (slots[slotTarget]?.files || []).forEach((entry) => {
                    const fileDetails = detailsByFileId[entry.id];
                    scanRemoteEntry(
                        entry,
                        fileDetails?.labelColumn,
                        slotTarget,
                        fileDetails?.labelsDelimiter
                    );
                });
            });
            return;
        }

        const targetsToCheck = [
            {
                column: detailsByTarget.dataset?.labelColumn,
                slotTargets: sharedDetailsMode ? ['dataset', 'benchmark'] : ['dataset'],
            },
            { column: detailsByTarget.benchmark?.labelColumn, slotTargets: ['benchmark'] },
        ];
        targetsToCheck.forEach(({ column, slotTargets }) => {
            if (!column) return;
            slotTargets.forEach((slotTarget) => {
                (slots[slotTarget]?.files || []).forEach((entry) => {
                    scanRemoteEntry(
                        entry,
                        column,
                        slotTarget,
                        detailsByTarget[slotTarget]?.labelsDelimiter
                    );
                });
            });
        });
    }, [
        slots,
        detailsByTarget,
        detailsByFileId,
        splitFileDetails,
        sharedDetailsMode,
        libraryEmptyScans,
        usecase?.client_id,
        usecase?.usecase_name,
        recordLibraryEmptyScan,
        isMultiLabel,
    ]);

    // Keep the parent-held cache in sync on every render so the latest state
    // is available when the step remounts after Back/Next navigation.
    useEffect(() => {
        if (!stateCacheRef) return;
        stateCacheRef.current = {
            slots,
            languages,
            shareDetails,
            detailsByTarget,
            detailsByFileId,
            defaultedFor: defaultedFor.current,
            extractedFor: extractedFor.current,
            delimiterDetectedFor: delimiterDetectedFor.current,
            languagesDetectedFor: languagesDetectedFor.current,
            seededFallback: lastSeededFallback.current,
        };
    });

    /**
     * Details target behind the primary (top) form. In shared mode it follows
     * the file that drives the form — the dataset when it has data, otherwise
     * the benchmark (mirrors `slotForTarget`). This is what makes column
     * auto-selection and label extraction visible when a benchmark is
     * uploaded first: its defaults land on the benchmark details, so the
     * shared form must read (and submit) that same target.
     */
    const primaryDetailsTarget = useMemo(() => {
        if (!showDatasetCard) return 'benchmark';
        if (!showBenchmarkCard || !shareDetails) return 'dataset';
        return slotHasLocalData(slots.dataset) || !slotHasLocalData(slots.benchmark)
            ? 'dataset'
            : 'benchmark';
    }, [showDatasetCard, showBenchmarkCard, shareDetails, slots]);

    // Pre-select Supported languages from the example-column cells we iterate
    // for previews (first N records). Only languages above 3% of that sample
    // are kept. Skips when there is no local content to scan (hydrated drafts
    // keep usecase.task_languages). Re-runs when the file set or example
    // column changes; manual edits stick until then.
    useEffect(() => {
        if (isModal) return;

        if (splitFileDetails) {
            const files = ['dataset', 'benchmark'].flatMap((target) =>
                (slots[target]?.files || []).filter(
                    (entry) =>
                        uploadEntryHasPreviewSamples(entry) &&
                        detailsByFileId[entry.id]?.exampleColumn
                )
            );
            if (!files.length) return;

            const signature = files
                .map((entry) => `${detailsByFileId[entry.id].exampleColumn}::${entry.id}`)
                .join('|');
            if (languagesDetectedFor.current === signature) return;

            const values = files.flatMap((entry) =>
                fieldValuesFromUploadEntry(
                    entry,
                    detailsByFileId[entry.id].exampleColumn,
                    LANGUAGE_DETECTION_SAMPLE_SIZE
                )
            );
            if (!values.length) return;

            languagesDetectedFor.current = signature;
            const detected = significantLanguagesFromTexts(values);
            setLanguages(detected.length ? detected : ['en']);
            return;
        }

        const target = primaryDetailsTarget;
        const exampleColumn = detailsByTarget[target]?.exampleColumn;
        if (!exampleColumn) return;

        let entries = slots[target]?.files || [];
        if (target === 'dataset' && !entries.length && sharedDetailsFallsBackToBenchmark) {
            entries = slots.benchmark?.files || [];
        }
        entries = entries.filter(uploadEntryHasPreviewSamples);
        if (!entries.length) return;

        const signature = `${exampleColumn}::${entries.map((entry) => entry.id).join('|')}`;
        if (languagesDetectedFor.current === signature) return;

        const values = entries.flatMap((entry) =>
            fieldValuesFromUploadEntry(entry, exampleColumn, LANGUAGE_DETECTION_SAMPLE_SIZE)
        );
        if (!values.length) return;

        languagesDetectedFor.current = signature;
        const detected = significantLanguagesFromTexts(values);
        setLanguages(detected.length ? detected : ['en']);
    }, [
        isModal,
        primaryDetailsTarget,
        detailsByTarget,
        detailsByFileId,
        splitFileDetails,
        slots,
        sharedDetailsFallsBackToBenchmark,
    ]);

    // The share-details checkbox is only shown while a benchmark is present
    // (local pick or already attached to the usecase); if it is removed, fall
    // back to shared details so a stale split form doesn't stick around.
    useEffect(() => {
        if (!hasBenchmarkAttached) {
            setShareDetails(true);
            setShareDetailsError(null);
        }
    }, [hasBenchmarkAttached]);

    // Classification/NER: once both files have finished label extraction, treat
    // differing label sets as incompatible with "share the same details".
    const extractedLabelsMismatch = useMemo(() => {
        if (!(isClassification || isNer) || !hasBenchmarkAttached) return false;
        const datasetDetails = detailsByTarget.dataset;
        const benchmarkDetails = detailsByTarget.benchmark;
        if (datasetDetails.labelsLoading || benchmarkDetails.labelsLoading) return false;
        // labelColumn stays undefined until defaults (or the user) pick one —
        // don't compare until both sides have attempted extraction.
        if (
            datasetDetails.labelColumn === undefined ||
            benchmarkDetails.labelColumn === undefined
        ) {
            return false;
        }
        // "No labels column" on both sides is a match; only compare real sets.
        if (datasetDetails.labelColumn === '' && benchmarkDetails.labelColumn === '') {
            return false;
        }
        // An empty side means extraction hasn't produced results yet — there
        // is a render window between the default labelColumn patch and the
        // extraction effect flipping labelsLoading on, and comparing during it
        // would auto-uncheck "share the same details" on identical files.
        const datasetLabels = datasetDetails.extractedLabels || [];
        const benchmarkLabels = benchmarkDetails.extractedLabels || [];
        if (!datasetLabels.length || !benchmarkLabels.length) return false;
        return !extractedLabelsEqual(datasetLabels, benchmarkLabels);
    }, [isClassification, isNer, hasBenchmarkAttached, detailsByTarget]);

    // Any task type: dataset and benchmark files with no columns in common
    // cannot share details — the shared form's example/label columns would be
    // submitted for a file that does not contain them and ingestion would
    // fail (e.g. train file with `article` + benchmark with `example`).
    // Per-file details (split mode) already allow different schemas.
    const sharedColumnsMismatch = useMemo(() => {
        if (splitFileDetails || !showDatasetCard || !showBenchmarkCard) return false;
        return crossTargetColumnsMismatch(slots.dataset?.files, slots.benchmark?.files);
    }, [
        splitFileDetails,
        showDatasetCard,
        showBenchmarkCard,
        slots.dataset?.files,
        slots.benchmark?.files,
    ]);

    // Columns / distinct-label scan still running on a visible target. The
    // shared form only shows the dataset's spinner, so a later benchmark pick
    // would otherwise sit idle until the mismatch message appears.
    const isExtractingLabels = useMemo(() => {
        if (!(isClassification || isNer)) return false;
        if (splitFileDetails) {
            return ['dataset', 'benchmark'].some((target) =>
                (slots[target]?.files || []).some((entry) =>
                    isLabelExtractionPending({
                        files: [entry],
                        details: detailsByFileId[entry.id],
                    })
                )
            );
        }
        const datasetPending =
            showDatasetCard &&
            isLabelExtractionPending({
                files: slots.dataset?.files,
                details: detailsByTarget.dataset,
            });
        const benchmarkPending =
            showBenchmarkCard &&
            isLabelExtractionPending({
                files: slots.benchmark?.files,
                details: detailsByTarget.benchmark,
            });
        return datasetPending || benchmarkPending;
    }, [
        isClassification,
        isNer,
        showDatasetCard,
        showBenchmarkCard,
        slots.dataset?.files,
        slots.benchmark?.files,
        detailsByTarget,
        detailsByFileId,
        splitFileDetails,
    ]);

    const isComparingSharedLabels =
        Boolean(shareDetails && hasBenchmarkAttached && showDatasetCard && showBenchmarkCard) &&
        isExtractingLabels;

    // Checkbox / split-form truth: checked by default; a known label or
    // column mismatch unchecks it (even before the auto-uncheck effects
    // clear state).
    const shareDetailsChecked =
        shareDetails && !extractedLabelsMismatch && !sharedColumnsMismatch;

    // Auto-uncheck when extracted labels diverge. Keep each file's own
    // extracted state (don't seed/wipe) so both Review Extracted Labels
    // sections stay populated after the split.
    useEffect(() => {
        if (!shareDetails || !extractedLabelsMismatch) return;
        setShareDetails(false);
        setShareDetailsError(SHARE_DETAILS_LABELS_MISMATCH_ERROR);
    }, [shareDetails, extractedLabelsMismatch]);

    // Auto-uncheck as soon as an uploaded pair turns out to have no columns
    // in common — each target then gets its own details form defaulted from
    // its own file's columns.
    useEffect(() => {
        if (!shareDetails || !sharedColumnsMismatch) return;
        setShareDetails(false);
        setShareDetailsError(SHARE_DETAILS_COLUMNS_MISMATCH_ERROR);
    }, [shareDetails, sharedColumnsMismatch]);

    // Generative tasks have no label-mapping UI — drop a stale mismatch error
    // left over from a prior classification session / hydration race. A live
    // column mismatch applies to every task type, so keep that message.
    useEffect(() => {
        if (isClassification || isNer) return;
        if (sharedColumnsMismatch) return;
        setShareDetailsError(null);
    }, [isClassification, isNer, sharedColumnsMismatch]);

    // Clear the blocking error once labels and columns become compatible
    // again. While either still differs, keep whatever message was set
    // (auto-uncheck or failed check).
    useEffect(() => {
        if (extractedLabelsMismatch || sharedColumnsMismatch) return;
        setShareDetailsError(null);
    }, [extractedLabelsMismatch, sharedColumnsMismatch]);

    // Unchecking "share the same details" seeds the other form from the shared
    // values so the split starts from what was configured (dataset → benchmark
    // normally; benchmark → dataset when a benchmark was uploaded first).
    // Checking is blocked when extracted labels differ or the files have no
    // columns in common — show an inline error instead.
    const handleShareDetailsChange = (checked) => {
        if (checked) {
            if (extractedLabelsMismatch) {
                setShareDetailsError(SHARE_DETAILS_LABELS_MISMATCH_ERROR);
                return;
            }
            if (sharedColumnsMismatch) {
                setShareDetailsError(SHARE_DETAILS_COLUMNS_MISMATCH_ERROR);
                return;
            }
            setShareDetailsError(null);
            setShareDetails(true);
            return;
        }
        // primaryDetailsTarget is still the shared source here — shareDetails
        // has not flipped yet.
        const source = primaryDetailsTarget;
        const seeded = source === 'dataset' ? 'benchmark' : 'dataset';
        setShareDetailsError(null);
        setShareDetails(false);
        extractedFor.current[seeded] = null;
        delimiterDetectedFor.current[seeded] = null;
        const sourceDetails = detailsByTarget[source];
        const guessedDelimiter =
            isMultiLabel && sourceDetails?.labelColumn
                ? guessLabelsDelimiterFromEntries(
                      slots[seeded]?.files || [],
                      sourceDetails.labelColumn
                  )
                : null;
        setDetailsByTarget((prev) => ({
            ...prev,
            [seeded]: {
                ...prev[source],
                extractedLabels: [],
                labelMappings: {},
                labelsLoading: false,
                labelsDelimiter: guessedDelimiter,
            },
        }));
        if (guessedDelimiter) clearDetailsFieldError(seeded, 'labelsDelimiter');
    };

    /** Persist task-type / labels / languages edits onto the created usecase. */
    const persistUsecase = useCallback(
        () =>
            new Promise((resolve, reject) => {
                if (!usecase?._id) {
                    resolve();
                    return;
                }
                data_from_server_post(
                    'update_llm_usecase',
                    {
                        usecase_id: usecase._id,
                        client_id: usecase.client_id,
                        usecase_name: usecase.usecase_name,
                        usecase_goal: usecase.usecase_goal,
                        group_name: usecase.group_name,
                        sub_group_name: usecase.sub_group_name,
                        task_type: taskType,
                        is_multilabel: isClassification && form.classificationType === 'multi',
                        ...fallbackPayloadFromSelect(form.fallbackLabel, {
                            isClassification,
                            classificationType: form.classificationType,
                        }),
                        task_languages: languages,
                        ...(isClassification && { manually_added_labels: form.labels }),
                        ...(isNer && { manually_added_entities: form.labels }),
                    },
                    (result) => {
                        if (result?.success) resolve();
                        else reject(new Error(result?.message || 'Error updating task'));
                    },
                    (error) => reject(new Error(error || 'Error updating task'))
                );
            }),
        [usecase, taskType, isClassification, isNer, form, languages]
    );

    /** complete-dataset-upload metadata for one target (and file, in split mode). */
    const getMetadata = useCallback(
        (target, entry) => {
            const fileEntry = entry || primaryFileEntry(slots[target]);
            let d;
            if (splitFileDetails && fileEntry?.id) {
                d = detailsByFileId[fileEntry.id] || detailsByTarget[target] || emptyDetails();
            } else {
                // Shared details only apply when both cards are in play; a
                // dataset- or benchmark-scoped modal always reads its own form.
                // The shared form's details live on `primaryDetailsTarget`
                // (benchmark when a benchmark was uploaded first).
                const detailsKey =
                    shareDetails && showDatasetCard && showBenchmarkCard
                        ? primaryDetailsTarget
                        : target;
                d = detailsByTarget[detailsKey];
            }
            const template = d.customInstructions ? d.promptTemplate.trim() : '';
            const dynamicFields = dynamicFieldsFromTemplate(template);
            const examplesAreWithPrompt = Boolean(template && dynamicFields.length);
            const optedOut = d.hasLabels === false;
            const submitAi = shouldSubmitAiLabelMappings(d);
            // Wizard AI path uses Business Goals labels/entities as the catalog
            // (identity mappings) — no separate "Generate labels with AI" UI.
            const taskLabelCatalog = (form.labels || [])
                .map((label) => (label || '').trim())
                .filter(Boolean);
            const labelMappings =
                submitAi && (isClassification || isNer)
                    ? Object.fromEntries(taskLabelCatalog.map((label) => [label, label]))
                    : optedOut
                      ? {}
                      : d.labelMappings || {};
            const emptyGenerateWithAi =
                !optedOut && isEmptyGenerateWithAi(d.emptyMultilabelHandling);
            const scanEntries =
                splitFileDetails && fileEntry ? [fileEntry] : slots[target]?.files || [];
            const thisTargetHasEmptyValues = scanUploadEntriesForEmptyField({
                entries: scanEntries,
                column: d.labelColumn,
                fileCache: emptyScanCacheRef.current,
                libraryEmptyScans,
                skipEntry: (item) => isAiGeneratedUploadEntry(item, usecase?.usecase_name),
            });
            // Shared empty-response choice applies only to files that actually
            // contain empty cells. A fully labeled dataset must not show or
            // run "Generating labels" just because the sibling benchmark does.
            const emptyOnlyAiForThisFile =
                emptyGenerateWithAi && thisTargetHasEmptyValues !== false;
            const generateResponsesWithAi =
                (optedOut &&
                    d.noLabelsMode !== NO_LABELS_MODE_UNLABELED &&
                    isGenerativeTask(taskType)) ||
                (emptyOnlyAiForThisFile && isGenerativeTask(taskType));
            // Wizard Generate-with-AI: upload now, arm LABEL_ONLY after Model
            // Config Next. Add Data (modal) has no arm step, so it must not
            // defer — otherwise LABEL_ONLY waits forever on label_generation_armed.
            // Empty-only AI labeling keeps the response column and fills blanks.
            // Agentic tasks only (both stay undefined otherwise so the
            // non-agentic payload is unchanged):
            // - `generate_trajectories`: the user answered "No" to having
            //   trajectories, so every row gets one from the teacher model
            //   after Model Configuration.
            // - `empty_trajectory_handling`: what to do with rows whose
            //   mapped trajectory column is empty.
            const generateTrajectories = isAgentic ? d.hasTrajectories === false : undefined;
            const emptyTrajectoryHandling =
                isAgentic && d.hasTrajectories === true
                    ? d.emptyTrajectoryHandling || EMPTY_TRAJECTORIES_DEFAULT
                    : undefined;
            // Trajectory generation is armed by the same Model Configuration
            // Next as LABEL_ONLY, so it defers in the wizard too.
            const wantsTrajectoryGeneration =
                Boolean(generateTrajectories) ||
                isEmptyTrajectoryGenerateWithAi(emptyTrajectoryHandling);
            const wantsAiLabeling =
                submitAi ||
                generateResponsesWithAi ||
                emptyOnlyAiForThisFile ||
                wantsTrajectoryGeneration;
            const deferLabelGeneration = shouldDeferLabelGeneration(
                !isModal,
                wantsAiLabeling
            );
            const fileColumnNames = splitFileDetails
                ? [...new Set(fileEntry?.columns || [])]
                : [
                      ...new Set(
                          (slots[target]?.files || []).flatMap((item) => item.columns || [])
                      ),
                  ];
            return {
                datasetOrigin: generateResponsesWithAi
                    ? DatasetOrigin.specific_ai_labeled
                    : d.dataCreation,
                exampleColumnName: d.exampleColumn || 'example',
                labelColumnName: optedOut ? '' : (d.labelColumn ?? ''),
                // Multilabel classification labels delimiter — sent as
                // `classification_labels_delimiter` on upload/clone.
                delimiter: isMultiLabel && !optedOut ? d.labelsDelimiter || null : null,
                fileColumnNames,
                labelMappings,
                generateResponsesWithAi,
                deferLabelGeneration,
                // Fallback label applies to single- and multi-label
                // classification; Best guess / No predictions mean none.
                rejectionLabelName:
                    (isSingleLabel || isMultiLabel) && !isFallbackSentinel(d.rejectionLabel)
                        ? d.rejectionLabel
                        : null,
                emptyMultilabelHandling:
                    emptyGenerateWithAi && thisTargetHasEmptyValues === false
                        ? emptyResponseDefault(isMultiLabel)
                        : d.emptyMultilabelHandling || emptyResponseDefault(isMultiLabel),
                examplesAreWithPrompt,
                ...(examplesAreWithPrompt && {
                    promptTemplate: template,
                    dynamicFieldsInPromptTemplate: dynamicFields,
                    // The saved Primary model input; older drafts without one
                    // fall back to the legacy heuristic.
                    exampleDynamicField: exampleDynamicFieldOf(d),
                }),
                // Agentic tasks: trajectory column + message keys + role
                // mapping, sent as `trajectory_*` form fields on upload.
                trajectoryMapping: isAgentic ? trajectoryMappingFromDetails(d) : null,
                generateTrajectories,
                emptyTrajectoryHandling,
            };
        },
        [
            shareDetails,
            showDatasetCard,
            showBenchmarkCard,
            primaryDetailsTarget,
            detailsByTarget,
            detailsByFileId,
            splitFileDetails,
            isSingleLabel,
            isMultiLabel,
            isClassification,
            isNer,
            isAgentic,
            taskType,
            form.labels,
            slots,
            libraryEmptyScans,
            usecase?.usecase_name,
            isModal,
        ]
    );

    const proceed = useCallback(async () => {
        setShowBenchmarkModal(false);
        setShowUnknownLabelsModal(false);
        setIsSaving(true);
        try {
            await persistUsecase();
            await upload.submit({ getMetadata });
            // Re-sync a changed response-column mapping (labels / entities /
            // summary / output) onto already-ingested clones, so Next→Back→
            // re-pick column→Next is reflected in the Data Editor without a
            // re-upload. Entries pending upload in this submit were just
            // ingested with the new mapping, so they are skipped; the backend
            // no-ops on clones whose stored column is unchanged.
            const supportsColumnSync = isClassification || isNer || isGenerative;
            if (supportsColumnSync && usecase?._id) {
                for (const target of ['dataset', 'benchmark']) {
                    if (target === 'dataset' && !showDatasetCard) continue;
                    if (target === 'benchmark' && !showBenchmarkCard) continue;
                    const attached =
                        (slots[target]?.files || []).some((entry) => entry.uploadName) ||
                        (target === 'benchmark'
                            ? (usecase?.benchmarks || []).length > 0
                            : (usecase?.datasets || []).length > 0);
                    if (!attached) continue;
                    const targetEntries = slots[target]?.files || [];
                    const syncTargets = [];
                    if (splitFileDetails) {
                        // Per-file details: sync each already-ingested file's
                        // own mapping onto its own clone.
                        targetEntries.forEach((entry) => {
                            if (!entry?.id || isPendingUploadEntry(entry)) return;
                            syncTargets.push({
                                details: detailsByFileId[entry.id] || detailsByTarget[target],
                                datasetName: entry.datasetName || null,
                            });
                        });
                    } else {
                        const allPending =
                            targetEntries.length > 0 &&
                            targetEntries.every(isPendingUploadEntry);
                        if (!allPending) {
                            const detailsKey =
                                shareDetails && showDatasetCard && showBenchmarkCard
                                    ? primaryDetailsTarget
                                    : target;
                            syncTargets.push({
                                details: detailsByTarget[detailsKey],
                                datasetName: null,
                            });
                        }
                    }
                    let clearedTarget = false;
                    for (const { details, datasetName } of syncTargets) {
                        // A changed Primary model input (Map prompt template
                        // modal, after ingest) re-points request_example at
                        // the newly picked dynamic field — its values are
                        // already stored per record. The backend no-ops when
                        // the stored primary is unchanged.
                        const savedExampleField = exampleDynamicFieldOf(details);
                        if (savedExampleField) {
                            await applyExampleFieldToDataset({
                                clientId: usecase.client_id,
                                usecaseId: usecase._id,
                                isBenchmark: target === 'benchmark',
                                exampleFieldName: savedExampleField,
                                datasetName,
                            });
                        }
                        const action = labelColumnSyncAction(details);
                        if (action === 'clear') {
                            // Post-ingest label stripping is classification-only;
                            // generative / NER opt-outs run through AI generation.
                            if (!isClassification || clearedTarget) continue;
                            clearedTarget = true;
                            await clearLabelColumnFromDataset({
                                clientId: usecase.client_id,
                                usecaseId: usecase._id,
                                isBenchmark: target === 'benchmark',
                            });
                            continue;
                        }
                        if (action !== 'apply') continue;
                        await applyLabelColumnToDataset({
                            clientId: usecase.client_id,
                            usecaseId: usecase._id,
                            isBenchmark: target === 'benchmark',
                            labelColumnName: details.labelColumn,
                            labelMappings: details.labelMappings || {},
                            delimiter: isMultiLabel
                                ? details.labelsDelimiter || null
                                : null,
                            datasetName,
                        });
                    }
                }
            }
            if (isModal) {
                refreshUsecases?.();
                onComplete?.();
            } else {
                refreshUsecases?.(() => onNext());
            }
        } catch (error) {
            sparkToast.error(
                error?.message ||
                    (isModal ? 'Failed to upload data' : 'Failed to save Data Collection step')
            );
            setIsSaving(false);
            return;
        }
        setIsSaving(false);
    }, [
        persistUsecase,
        upload,
        getMetadata,
        refreshUsecases,
        onNext,
        onComplete,
        isModal,
        isClassification,
        isNer,
        isGenerative,
        isMultiLabel,
        slots,
        usecase,
        shareDetails,
        showDatasetCard,
        showBenchmarkCard,
        primaryDetailsTarget,
        detailsByTarget,
        detailsByFileId,
        splitFileDetails,
    ]);

    const hasDatasetAttached = Boolean(
        slotHasLocalData(slots.dataset) || (usecase?.datasets || []).length > 0
    );
    const hasPendingUpload = useMemo(
        () =>
            visibleTargets.some((target) =>
                (slots[target]?.files || []).some(isPendingUploadEntry)
            ),
        [slots, visibleTargets]
    );

    const unmatchedLabelSources = useMemo(
        () =>
            unknownLabelSources({
                detailsByTarget,
                availableLabels: form.labels || [],
                shareDetails,
                showDatasetCard,
                showBenchmarkCard,
                primaryDetailsTarget,
            }),
        [
            detailsByTarget,
            form.labels,
            shareDetails,
            showDatasetCard,
            showBenchmarkCard,
            primaryDetailsTarget,
        ]
    );

    // Data Overview drag-and-drop: seed the scoped card with every dropped file.
    const consumedDropRef = useRef(null);
    useEffect(() => {
        const files = Array.isArray(initialDroppedFiles)
            ? initialDroppedFiles.filter((f) => f instanceof File)
            : [];
        if (files.length === 0) return;
        if (consumedDropRef.current === initialDroppedFiles) return;
        const target = showBenchmarkCard && !showDatasetCard ? 'benchmark' : 'dataset';
        consumedDropRef.current = initialDroppedFiles;
        onInitialDroppedFilesConsumed?.();
        void upload.pickFiles(target, files);
        // Only re-run when a new dropped-file batch arrives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDroppedFiles]);

    // Library pick: seed the scoped card and autofill known column mappings.
    const consumedLibraryPickRef = useRef(null);
    useEffect(() => {
        const picks = Array.isArray(pendingLibraryPick?.picks)
            ? pendingLibraryPick.picks
            : pendingLibraryPick?.option
              ? [{ target: pendingLibraryPick.target, option: pendingLibraryPick.option }]
              : [];
        if (!picks.length) return;
        if (consumedLibraryPickRef.current === pendingLibraryPick) return;
        consumedLibraryPickRef.current = pendingLibraryPick;
        const fallbackTarget =
            showBenchmarkCard && !showDatasetCard ? 'benchmark' : 'dataset';
        onPendingLibraryPickConsumed?.();
        void (async () => {
            const autofilledTargets = new Set();
            for (const pick of picks) {
                const option = pick?.option;
                if (!option) continue;
                const target = pick.target || fallbackTarget;
                const result = await upload.pickFromLibrary(target, option);
                if (!result) continue;
                const skipTargetAutofill = autofilledTargets.has(target);
                if (!skipTargetAutofill) autofilledTargets.add(target);
                const mappings = result.columnMappings || {};
                // Prefill mappings like the upload flow: a mapping stored on the
                // source dataset wins, then a fuzzy task-label match (case /
                // separators / plural), then identity.
                const storedMappings = result.storedLabelMappings || {};
                const distinctLabels = result.distinctLabels || [];
                const matchTaskLabel = buildTaskLabelMatcher(form.labels);
                const labelMappings = distinctLabels.length
                    ? Object.fromEntries(
                          distinctLabels.map((label) => [
                              label,
                              storedMappings[label] ?? matchTaskLabel(label) ?? label,
                          ])
                      )
                    : storedMappings;
                const libraryDetailsPatch = {
                    exampleColumn: mappings.example_column_name || null,
                    ...(mappings.label_column_name !== undefined
                        ? { labelColumn: mappings.label_column_name || '' }
                        : {}),
                    extractedLabels: distinctLabels,
                    labelMappings,
                    labelsLoading: false,
                };
                if (!skipTargetAutofill) {
                    patchDetails(target, libraryDetailsPatch);
                }
                if (result.id) {
                    patchFileDetails(result.id, libraryDetailsPatch);
                    if (result.columns?.length) {
                        defaultedFor.current[`file:${result.id}:${result.columns.join(',')}`] =
                            true;
                    }
                    if (mappings.label_column_name) {
                        extractedFor.current[result.id] =
                            `${mappings.label_column_name}::${result.id}`;
                    }
                }
                // Seed the empty-labels answer fetched with the sample so the
                // effect above doesn't re-query for the stored column.
                if (mappings.label_column_name) {
                    recordLibraryEmptyScan(
                        result.id,
                        mappings.label_column_name,
                        result.hasEmptyLabelValues ?? null
                    );
                }
                // Same for the agentic trajectory column, when the library
                // dataset stores one.
                if (isAgentic && mappings.trajectory_column_name) {
                    recordLibraryEmptyScan(
                        result.id,
                        mappings.trajectory_column_name,
                        result.hasEmptyTrajectoryValues ?? null,
                        EMPTY_VALUE_KIND_TRAJECTORY
                    );
                }
                // Mark defaults/extraction as done so the file-based effects
                // don't wipe the autofilled library columns.
                const entryKey = `${target}:${result.id || result.datasetName}`;
                if (!skipTargetAutofill) {
                    defaultedFor.current[entryKey] = true;
                    if (mappings.label_column_name) {
                        extractedFor.current[target] =
                            `${mappings.label_column_name}::${result.id}`;
                    }
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingLibraryPick]);

    // Hugging Face pick: attach pending files; column mapping stays on this step.
    const consumedHuggingFacePickRef = useRef(null);
    useEffect(() => {
        if (!pendingHuggingFacePick?.datasetId) return;
        if (consumedHuggingFacePickRef.current === pendingHuggingFacePick) return;
        consumedHuggingFacePickRef.current = pendingHuggingFacePick;
        onPendingHuggingFacePickConsumed?.();
        void upload.pickFromHuggingFace(pendingHuggingFacePick);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingHuggingFacePick]);

    // Generate-with-AI attaches `{usecase}_ai` while the panel is already
    // mounted (hydration already ran empty). Fold the new usecase.datasets
    // name into the slot + fill LLM-created / example / response columns.
    const consumedAttachedNamesRef = useRef(new Set());
    const consumedAttachedUsecaseIdRef = useRef(usecase?._id);
    useEffect(() => {
        if (isModal && hideUploadSection) return undefined;
        if (consumedAttachedUsecaseIdRef.current !== usecase?._id) {
            consumedAttachedUsecaseIdRef.current = usecase?._id;
            consumedAttachedNamesRef.current = new Set();
        }
        const attached = {
            dataset: showDatasetCard ? usecase?.datasets || [] : [],
            benchmark: showBenchmarkCard ? usecase?.benchmarks || [] : [],
        };
        const missing = [];
        ['dataset', 'benchmark'].forEach((target) => {
            (attached[target] || []).forEach((name) => {
                if (!name) return;
                const consumeKey = `${target}:${name}`;
                if (consumedAttachedNamesRef.current.has(consumeKey)) return;
                if (slotCoversDatasetName(slots[target]?.files, name)) {
                    consumedAttachedNamesRef.current.add(consumeKey);
                    return;
                }
                missing.push({ target, name, consumeKey });
            });
        });
        if (!missing.length) return undefined;

        missing.forEach(({ consumeKey }) => consumedAttachedNamesRef.current.add(consumeKey));
        let cancelled = false;
        (async () => {
            const docs = await fetchUsecaseDatasetInfos(usecase);
            if (cancelled) return;
            const docsByName = new Map((docs || []).map((doc) => [doc.dataset, doc]));
            missing.forEach(({ target, name }) => {
                const doc = docsByName.get(name) || { dataset: name };
                const isAi = isAiGeneratedDataset({
                    dataCreationType: doc.data_creation_type,
                    datasetName: name,
                    usecaseName: usecase?.usecase_name,
                });
                const dataCreationType =
                    doc.data_creation_type ||
                    (isAiGeneratedDatasetName(name, usecase?.usecase_name)
                        ? DatasetOrigin.specific_ai_fully_generated
                        : undefined);
                const applied = applyAiGeneratedColumnDefaults({
                    columns: [
                        ...(doc.file_column_names || []),
                        ...(doc.extra_params_keys || []),
                    ],
                    mappings: doc.column_mappings || {},
                    taskType,
                    dataCreationType,
                    datasetName: name,
                    usecaseName: usecase?.usecase_name,
                });
                const entry = {
                    ...emptyFileEntry,
                    id: `hydrated-${name}`,
                    uploadName: stripCloneSuffix(name) || name,
                    datasetName: name,
                    columns: applied.columns,
                    dataCreationType,
                    ...sourceFieldsFromDatasetInfoDoc(doc),
                };
                upload.attachExistingEntries(target, [entry]);
                const current = detailsByTarget[target];
                const nextPatch = {};
                const uiCreation =
                    dataCreationForUi(dataCreationType) ||
                    (isAi ? LLM_CREATED_DATA_CREATION : null);
                if (!current?.dataCreation && uiCreation) nextPatch.dataCreation = uiCreation;
                if (!current?.exampleColumn && applied.mappings.example_column_name) {
                    nextPatch.exampleColumn = applied.mappings.example_column_name;
                }
                if (!current?.labelColumn && applied.mappings.label_column_name) {
                    nextPatch.labelColumn = applied.mappings.label_column_name;
                }
                if (isAi && current?.hasLabels === false) nextPatch.hasLabels = true;
                if (isAi && isMultiLabel && !current?.labelsDelimiter) {
                    nextPatch.labelsDelimiter = 'list';
                }
                if (
                    isAi &&
                    (isClassification || isNer) &&
                    !current?.extractedLabels?.length
                ) {
                    const labels = knownLabelsForAiGeneration({
                        formLabels: form.labels,
                        usecase,
                        taskType,
                    });
                    if (labels.length) {
                        nextPatch.extractedLabels = labels;
                        nextPatch.labelMappings = identityLabelMappings(labels);
                        nextPatch.labelsLoading = false;
                    }
                }
                if (Object.keys(nextPatch).length) {
                    patchDetails(target, nextPatch);
                    if (entry.id) patchFileDetails(entry.id, nextPatch);
                }
            });
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        usecase?.datasets,
        usecase?.benchmarks,
        usecase?._id,
        slots,
        taskType,
        isMultiLabel,
        isClassification,
        isNer,
        form.labels,
    ]);

    // Clear the upload-card error once the required target has data.
    useEffect(() => {
        const cleared =
            (showDatasetCard && hasDatasetAttached) ||
            (showBenchmarkCard &&
                !showDatasetCard &&
                (slotHasLocalData(slots.benchmark) ||
                    (usecase?.benchmarks || []).length > 0 ||
                    hasPendingUpload));
        if (!cleared) return;
        setErrors((prev) => {
            if (!prev.dataset) return prev;
            const { dataset: _removed, ...rest } = prev;
            return rest;
        });
    }, [
        hasDatasetAttached,
        hasPendingUpload,
        showDatasetCard,
        showBenchmarkCard,
        slots.benchmark,
        usecase?.benchmarks,
    ]);

    const validateDetailsTarget = useCallback(
        (target, details) => {
            const fieldErrors = {};
            if (!details.exampleColumn) {
                fieldErrors.exampleColumn = 'Please select an example field';
            }
            if ((isClassification || isNer) && details.hasLabels !== false) {
                if (details.labelColumn === undefined) {
                    fieldErrors.labelColumn = 'Please select a labels column';
                }
            }
            // Multilabel classification: the delimiter drives label splitting
            // during extraction and processing — require one when a labels
            // column is selected and auto-detection could not determine it.
            if (
                isMultiLabel &&
                details.hasLabels !== false &&
                details.labelColumn &&
                !details.labelsDelimiter
            ) {
                fieldErrors.labelsDelimiter = 'Please select a labels delimiter';
            }
            // Multilabel classification: the delimiter drives label splitting
            // during extraction and processing — require one when a labels
            // column is selected and auto-detection could not determine it.
            if (
                isMultiLabel &&
                details.hasLabels !== false &&
                details.labelColumn &&
                !details.labelsDelimiter
            ) {
                fieldErrors.labelsDelimiter = 'Please select a labels delimiter';
            }
            if (isGenerative && details.hasLabels !== false && !details.labelColumn) {
                fieldErrors.labelColumn = isContentGeneration
                    ? 'Please select an output field'
                    : 'Please select a summary field';
            }
            const needsAiCatalog =
                (details.hasLabels === false &&
                    details.noLabelsMode !== NO_LABELS_MODE_UNLABELED) ||
                (details.hasLabels !== false &&
                    isEmptyGenerateWithAi(details.emptyMultilabelHandling));
            if (needsAiCatalog && (isClassification || isNer)) {
                const catalog = (form.labels || [])
                    .map((label) => (label || '').trim())
                    .filter(Boolean);
                if (catalog.length < 1) {
                    fieldErrors.labelColumn = isNer
                        ? 'Add at least one entity in Business Goals before generating with AI'
                        : 'Add at least one label in Business Goals before generating with AI';
                }
            }
            // Agentic tasks that answered Yes to trajectories: messages /
            // role / content picked and every role value mapped.
            if (isAgentic) {
                Object.assign(fieldErrors, trajectoryDetailsErrors(details));
            }
            return fieldErrors;
        },
        [
            isClassification,
            isNer,
            isGenerative,
            isContentGeneration,
            isMultiLabel,
            isAgentic,
            form.labels,
        ]
    );

    // The details forms currently rendered: per file in split mode, per
    // target otherwise (mirrors the validation walk in handleNext).
    const collectActiveDetails = () => {
        const activeDetails = [];
        if (splitFileDetails) {
            ['dataset', 'benchmark'].forEach((target) => {
                (slots[target]?.files || []).forEach((entry) => {
                    if (!entry?.id) return;
                    const details = detailsByFileId[entry.id];
                    if (details) activeDetails.push(details);
                });
            });
            return activeDetails;
        }
        const targets =
            shareDetails || !showBenchmarkCard || !showDatasetCard
                ? [primaryDetailsTarget]
                : ['dataset', 'benchmark'];
        targets.forEach((target) => {
            const details = detailsByTarget[target];
            if (details) activeDetails.push(details);
        });
        return activeDetails;
    };

    // Agentic tasks that answered No to trajectories get them generated with
    // AI, which needs a configured model provider: with none, the hint under
    // the question links to Settings and Next stays disabled until one is
    // added (Figma 4024:24838). Only once a details form is on screen.
    const hasAnyFileForDetails = isModal
        ? hasPendingUpload
        : Boolean(
              (showDatasetCard &&
                  (slotHasLocalData(slots.dataset) || (usecase?.datasets || []).length > 0)) ||
                  (showBenchmarkCard &&
                      (slotHasLocalData(slots.benchmark) ||
                          (usecase?.benchmarks || []).length > 0))
          );
    const trajectoriesNeedProvider =
        isAgentic &&
        hasNoProviders &&
        hasAnyFileForDetails &&
        collectActiveDetails().some((details) => details.hasTrajectories === false);

    const handleNext = () => {
        const nextErrors = {};
        // Wizard always requires a dataset. Modal scoped to benchmark does not.
        if (showDatasetCard && !hasDatasetAttached) {
            nextErrors.dataset = 'Please upload a dataset';
        }
        if (isModal && showBenchmarkCard && !showDatasetCard) {
            const hasBenchmark =
                slotHasLocalData(slots.benchmark) || (usecase?.benchmarks || []).length > 0;
            if (!hasBenchmark && !hasPendingUpload) {
                nextErrors.dataset = 'Please upload a benchmark';
            }
        }
        if (isModal && !hasPendingUpload && Object.keys(nextErrors).length === 0) {
            // Nothing new to upload — close without a no-op submit.
            onComplete?.();
            return;
        }

        const hasAnyAttached = Boolean(
            (showDatasetCard && hasDatasetAttached) ||
                (showBenchmarkCard &&
                    (slotHasLocalData(slots.benchmark) ||
                        (usecase?.benchmarks || []).length > 0))
        );
        if (hasAnyAttached) {
            const detailsErrors = {};
            if (splitFileDetails) {
                ['dataset', 'benchmark'].forEach((target) => {
                    (slots[target]?.files || []).forEach((entry) => {
                        if (!entry?.id) return;
                        const details = detailsByFileId[entry.id];
                        if (!details) return;
                        const fieldErrors = validateDetailsTarget(target, details);
                        if (Object.keys(fieldErrors).length) {
                            detailsErrors[entry.id] = fieldErrors;
                        }
                    });
                });
            } else {
                const targets =
                    shareDetails || !showBenchmarkCard || !showDatasetCard
                        ? [primaryDetailsTarget]
                        : ['dataset', 'benchmark'];
                targets.forEach((target) => {
                    const fieldErrors = validateDetailsTarget(target, detailsByTarget[target]);
                    if (Object.keys(fieldErrors).length) detailsErrors[target] = fieldErrors;
                });
                // Shared details are submitted for every uploaded file across
                // both targets — a pick that is missing from any of those
                // files would fail ingestion, so block Next with a field
                // error (last resort; the effects above normally auto-split
                // or clamp before this can happen).
                if (sharedDetailsSpansBothTargets) {
                    const details = detailsByTarget[primaryDetailsTarget] || {};
                    const loadedFiles = [
                        ...(slots.dataset?.files || []),
                        ...(slots.benchmark?.files || []),
                    ].filter(
                        (item) => !item.columnsLoading && (item.columns || []).length
                    );
                    const missingFromAFile = (column) =>
                        Boolean(column) &&
                        loadedFiles.some(
                            (item) => !normalizeColumnList(item.columns).includes(column)
                        );
                    const sharedErrors = detailsErrors[primaryDetailsTarget] || {};
                    if (!sharedErrors.exampleColumn && missingFromAFile(details.exampleColumn)) {
                        sharedErrors.exampleColumn = `Column "${details.exampleColumn}" is not present in both the Dataset and Benchmark files`;
                    }
                    if (
                        !sharedErrors.labelColumn &&
                        details.hasLabels !== false &&
                        missingFromAFile(details.labelColumn)
                    ) {
                        sharedErrors.labelColumn = `Column "${details.labelColumn}" is not present in both the Dataset and Benchmark files`;
                    }
                    if (Object.keys(sharedErrors).length) {
                        detailsErrors[primaryDetailsTarget] = sharedErrors;
                    }
                }
            }
            if (Object.keys(detailsErrors).length) nextErrors.details = detailsErrors;
        }

        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) {
            const { title, content } = validationToast(nextErrors, {
                isNer,
                isGenerative,
                isContentGeneration,
            });
            // Detailed error toast, bottom-right — same as Business Goals
            // (Figma 3423-15453).
            sparkToast.error(title, { content, detailed: true, duration: 5000 });
            return;
        }

        if (isExtractingLabels) return;

        // A Generate-with-AI selection (no-labels mode or empty-response
        // handling) needs a model provider to run after upload. With none
        // configured, block Next with the Add-credentials warning — walks the
        // same active details forms as validation (per file in split mode,
        // per target otherwise).
        if (hasNoProviders) {
            const activeDetails = collectActiveDetails();
            // Agentic No-trajectories answer: the Settings hint under the
            // question already explains the block (Next is disabled in the
            // wizard heading); the modal footer falls back to the warning.
            if (activeDetails.some(detailsWantAiGeneration) || trajectoriesNeedProvider) {
                setShowNoProviderModal(true);
                return;
            }
        }

        // "Update labels in task" / toggling labels on this step can leave the
        // Business Goals "What matters most" choice pointing at a label the
        // task no longer has. The backend would silently drop it (and Train
        // would fall back to "All labels"); block instead so the user fixes
        // the choice or the labels.
        const taskLabels = Array.isArray(form?.labels) ? form.labels : [];
        const missingOptimizeOn = missingOptimizeOnLabel(
            labelPriorityOptimizeOnLabel(form?.labelPriority),
            taskLabels
        );
        if (missingOptimizeOn) {
            setOptimizeOnMismatch({ label: missingOptimizeOn, taskLabels });
            return;
        }

        if (unmatchedLabelSources.length) {
            setShowUnknownLabelsModal(true);
            return;
        }

        const hasBenchmark =
            slotHasLocalData(slots.benchmark) || (usecase?.benchmarks || []).length > 0;
        // Only the wizard prompts when continuing without a dedicated benchmark.
        if (!isModal && showDatasetCard && hasDatasetAttached && !hasBenchmark) {
            setShowBenchmarkModal(true);
            return;
        }
        proceed();
    };

    const continuePastUnknownLabels = () => {
        setShowUnknownLabelsModal(false);
        const hasBenchmark =
            slotHasLocalData(slots.benchmark) || (usecase?.benchmarks || []).length > 0;
        if (!isModal && showDatasetCard && hasDatasetAttached && !hasBenchmark) {
            setShowBenchmarkModal(true);
            return;
        }
        proceed();
    };

    // "Add credentials" on the no-provider warning: deep-link to Settings >
    // Model Providers with a `from` state so its back button returns here. In
    // the wizard the navigation unmounts the page and its in-memory step
    // cache (including files not yet uploaded — upload happens on Next), so
    // the snapshot is stashed first and useDataCollectionHydration restores
    // it on remount.
    const handleAddCredentials = () => {
        setShowNoProviderModal(false);
        if (!isModal && usecase?._id && stateCacheRef?.current) {
            stashDataCollectionState(usecase._id, stateCacheRef.current);
        }
        navigate('/settings?tab=providers', {
            state: { from: `${location.pathname}${location.search}${location.hash}` },
        });
    };

    const availableLabels = form.labels || [];
    const replaceLabelOptions = useMemo(
        () =>
            unionLabels([
                availableLabels,
                detailsByTarget.dataset?.extractedLabels,
                detailsByTarget.benchmark?.extractedLabels,
                ...Object.values(detailsByFileId).map((details) => details?.extractedLabels),
            ]),
        [
            availableLabels,
            detailsByTarget.dataset?.extractedLabels,
            detailsByTarget.benchmark?.extractedLabels,
            detailsByFileId,
        ]
    );

    const handleToggleTaskLabel = useCallback(
        (label) => {
            if (!onFormChange || !label) return;
            const current = form.labels || [];
            const next = current.includes(label)
                ? current.filter((item) => item !== label)
                : [...current, label];
            onFormChange({ ...form, labels: next });
        },
        [form, onFormChange]
    );

    // "Update labels in task" (missing-labels Fix menu): add the unknown
    // extracted labels. In the create-task wizard also drop catalog labels
    // that appear in none of the uploaded files; the Add Data modal on an
    // existing task only adds — files uploaded to the task earlier may cover
    // labels this modal never sees, so nothing may be removed. Walks the same
    // active details forms as getMetadata — per file in split mode, per
    // target otherwise.
    const handleAddTaskLabels = useCallback(
        (labels) => {
            if (!onFormChange) return;
            const detailsList = [];
            ['dataset', 'benchmark'].forEach((target) => {
                if (target === 'dataset' && !showDatasetCard) return;
                if (target === 'benchmark' && !showBenchmarkCard) return;
                if (splitFileDetails) {
                    (slots[target]?.files || []).forEach((entry) => {
                        if (!entry?.id) return;
                        detailsList.push(detailsByFileId[entry.id] || detailsByTarget[target]);
                    });
                    return;
                }
                const detailsKey =
                    shareDetails && showDatasetCard && showBenchmarkCard
                        ? primaryDetailsTarget
                        : target;
                detailsList.push(detailsByTarget[detailsKey]);
            });
            const next = syncTaskLabelCatalog({
                catalog: form.labels,
                addedLabels: labels,
                detailsList,
                fallbackLabel: form.fallbackLabel,
                removeUncovered: !isModal,
            });
            onFormChange({ ...form, labels: next });
        },
        [
            form,
            onFormChange,
            slots,
            detailsByTarget,
            detailsByFileId,
            splitFileDetails,
            shareDetails,
            showDatasetCard,
            showBenchmarkCard,
            primaryDetailsTarget,
            isModal,
        ]
    );

    const openAddFilesPicker = useCallback((target) => {
        addFilesTargetRef.current = target;
        addFilesInputRef.current?.click();
    }, []);

    if (!usecase) return null;

    // Local pick, post-upload slot metadata, or files already on the usecase
    // (after Next → Back) all count as "has data" for the details sections.
    // Modal: only after a new local pick — already-attached chips don't carry
    // column samples, so showing an empty details form would be misleading.
    const hasAnyFile = hasAnyFileForDetails;
    const hasBenchmarkFile = hasBenchmarkAttached;
    const showShareDetailsToggle =
        showDatasetCard &&
        showBenchmarkCard &&
        hasBenchmarkFile &&
        !splitFileDetails &&
        !hideShareDetailsToggle;
    // Split mode ("share the same details" unchecked): the dataset form stays
    // in the top group and the benchmark form renders below the Data Details
    // section. Per-file split (2+ files in a slot) uses headings instead.
    const splitBenchmarkDetails =
        !splitFileDetails && showDatasetCard && showBenchmarkCard && !shareDetails;

    const renderDetailsForm = (target, { suffix = '', entry = null, heading = '' } = {}) => {
        const isFileForm = Boolean(splitFileDetails && entry?.id);
        const files = isFileForm ? [entry] : filesForDetailsTarget(target) || [];
        // Selectable columns come from every file the form's picks will be
        // applied to — across both targets in shared-details mode. Previews
        // and truncation warnings still follow this target's own files.
        const columnsFiles = isFileForm ? [entry] : columnsFilesForTarget(target);
        const overlap = overlapForFiles(columnsFiles);
        const availableColumns = isFileForm
            ? entry.columns
            : overlap.status === COLUMN_OVERLAP.partial ||
                overlap.status === COLUMN_OVERLAP.identical
              ? overlap.shared
              : undefined;
        const slot = isFileForm ? entry : slotForTarget(target);
        // Label/entity extraction previews only the first 20MB of oversized
        // files (fetchFileLabelsWithUpload truncates; after upload only a
        // content chunk remains) — surface that so the partial preview isn't
        // mistaken for the full file.
        const previewTruncated = files.some(
            (fileEntry) =>
                (fileEntry.sizeBytes ?? fileEntry.file?.size ?? 0) > MAX_PREVIEW_FILE_SIZE_BYTES
        );
        const details = isFileForm
            ? detailsByFileId[entry.id] ||
              (() => {
                  const slotFiles = slots[target]?.files || [];
                  const index = slotFiles.findIndex((file) => file.id === entry.id);
                  const targetDetails = detailsByTarget[target] || emptyDetails();
                  if (index === 0 && isDetailsConfigured(targetDetails)) {
                      return targetDetails;
                  }
                  return {
                      ...emptyDetails(),
                      rejectionLabel: targetDetails.rejectionLabel ?? null,
                      emptyMultilabelHandling: targetDetails.emptyMultilabelHandling ?? null,
                  };
              })()
            : detailsByTarget[target];
        const detailsKey = isFileForm ? entry.id : target;
        // Shared form is backed by the dataset; keep its mapping spinner up
        // while the hidden benchmark file is still being read/extracted.
        const showPeerLoading =
            !isFileForm &&
            isComparingSharedLabels &&
            target === primaryDetailsTarget &&
            !details?.labelsLoading;
        const formNode = (
            <DatasetDetailsForm
                titleSuffix={isFileForm ? '' : suffix}
                taskType={taskType}
                isMultilabel={isMultiLabel}
                slot={slot}
                details={{
                    ...details,
                    labelsLoading: Boolean(details?.labelsLoading || showPeerLoading),
                }}
                onPatch={(patch) =>
                    isFileForm ? patchFileDetails(entry.id, patch) : patchDetails(target, patch)
                }
                availableLabels={availableLabels}
                replaceLabelOptions={replaceLabelOptions}
                onLabelColumnChange={handleLabelColumnChange(target, isFileForm ? entry.id : null)}
                onToggleTaskLabel={handleToggleTaskLabel}
                errors={errors.details?.[detailsKey] || {}}
                showEmptyResponsesSection={
                    isFileForm
                        ? showEmptyResponsesByFileId[entry.id] !== false
                        : showEmptyResponsesByTarget[target]
                }
                showEmptyTrajectoriesSection={
                    isFileForm
                        ? showEmptyTrajectoriesByFileId[entry.id] !== false
                        : showEmptyTrajectoriesByTarget[target]
                }
                availableColumns={availableColumns}
                columnsLoading={
                    isFileForm
                        ? Boolean(entry.columnsLoading)
                        : columnsFiles.some((fileEntry) => fileEntry.columnsLoading)
                }
                preferUnlabeledOnOptOut={Boolean(slot?.uploadName && !slot?.file)}
                previewTruncated={previewTruncated}
                isExistingTask={isModal}
                isAgentic={isAgentic}
                hasNoProviders={hasNoProviders}
                providersLoading={isProvidersCatalogLoading}
                onOpenProviderSettings={handleAddCredentials}
                onAddTaskLabels={handleAddTaskLabels}
                onGenerateNewData={() => onOpenGenerateAi?.()}
                onAddMoreFiles={() => openAddFilesPicker(target)}
            />
        );
        if (!heading) return formNode;
        return (
            <div className="cw-dc-file-details-section">
                <h3 className="cw-dc-file-details-heading">{heading}</h3>
                {formNode}
            </div>
        );
    };

    const showSharedColumnsWarning =
        !splitFileDetails &&
        ((showDatasetCard &&
            overlapForFiles(slots.dataset?.files).status === COLUMN_OVERLAP.partial) ||
            (showBenchmarkCard &&
                overlapForFiles(slots.benchmark?.files).status === COLUMN_OVERLAP.partial) ||
            (sharedDetailsSpansBothTargets &&
                overlapForFiles(columnsFilesForTarget(primaryDetailsTarget)).status ===
                    COLUMN_OVERLAP.partial));

    const isBusy = isSaving || upload.isSubmitting;
    const nextLoading = isBusy || isExtractingLabels;
    const nextLoadingLabel = isBusy
        ? 'Saving…'
        : isNer
          ? 'Extracting entities…'
          : 'Extracting labels…';

    return (
        <div
            className={[
                'cw-step',
                'cw-step--data-collection',
                isModal ? 'cw-step--data-collection-modal' : '',
                'fs-unmask',
            ]
                .filter(Boolean)
                .join(' ')}
        >
            {!isModal && (
                <WizardStepHeading
                    title="Data Collection"
                    taskName={taskName}
                    onBack={onBack}
                    onNext={handleNext}
                    nextDisabled={trajectoriesNeedProvider}
                    nextLoading={nextLoading}
                    nextLoadingLabel={nextLoadingLabel}
                    nextDisableWhenReadOnly
                />
            )}

            {!(isModal && hideUploadSection) && (
                <>
                    <DataUploadSection
                        taskType={taskType}
                        isMultilabel={isMultiLabel}
                        isAgentic={isAgentic}
                        slots={slots}
                        uploadedNames={{
                            dataset: usecase?.datasets || [],
                            benchmark: usecase?.benchmarks || [],
                        }}
                        pickFiles={upload.pickFiles}
                        onRemovePending={handleRemovePending}
                        onRemoveUploaded={handleRemoveUploaded}
                        onOpenLibrary={openLibrary}
                        onOpenHuggingFace={onOpenHuggingFace}
                        onGenerateWithAi={onOpenGenerateAi}
                        onOpenRetrievalAgent={onOpenRetrievalAgent}
                        datasetError={errors.dataset}
                        columnErrorByTarget={columnErrorByTarget}
                        onDismissColumnError={(target) =>
                            setColumnErrorByTarget((prev) => ({ ...prev, [target]: null }))
                        }
                        visibleTargets={visibleTargets}
                        labelsLoadingByTarget={{
                            dataset: splitFileDetails
                                ? (slots.dataset?.files || []).some(
                                      (entry) => detailsByFileId[entry.id]?.labelsLoading
                                  )
                                : Boolean(detailsByTarget.dataset?.labelsLoading),
                            benchmark: splitFileDetails
                                ? (slots.benchmark?.files || []).some(
                                      (entry) => detailsByFileId[entry.id]?.labelsLoading
                                  )
                                : Boolean(detailsByTarget.benchmark?.labelsLoading),
                        }}
                        usecaseName={usecase?.usecase_name}
                    />

                    <div className="cw-dc-divider cw-dc-divider--section" />
                </>
            )}

            {isModal && hideUploadSection && !hasAnyFile && (
                <div className="cw-dc-file-preparing" role="status" aria-live="polite">
                    <Spinner size="small" />
                    <span>Reading file…</span>
                </div>
            )}

            {hasAnyFile && (
                <>
                    {!isModal && (
                        <div className="cw-dc-languages-block">
                            <div className="cw-dc-field-row">
                                <div className="cw-dc-field-row__label">
                                    <h3 className="cw-dc-section-title">Languages</h3>
                                    <p className="cw-dc-field-row__desc">
                                        Choose the languages that matter for your task for the
                                        model to tailor its training to.
                                    </p>
                                </div>
                                <div className="cw-dc-field-row__controls">
                                    <div className="cw-dc-select-360">
                                        <LanguageSelector
                                            label="Supported languages"
                                            showTooltip={false}
                                            required={false}
                                            value={languages}
                                            onChange={setLanguages}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {splitFileDetails ? (
                        [
                            ...(showDatasetCard ? datasetFiles : []),
                            ...(showBenchmarkCard ? benchmarkFiles : []),
                        ].map((entry, index) => {
                            const isBenchmark = (showBenchmarkCard ? benchmarkFiles : []).some(
                                (file) => file.id === entry.id
                            );
                            const kind = isBenchmark ? 'benchmark' : 'dataset';
                            const kindFiles = isBenchmark ? benchmarkFiles : datasetFiles;
                            const kindIndex =
                                kindFiles.findIndex((file) => file.id === entry.id) + 1;
                            return (
                                <React.Fragment key={entry.id}>
                                    {index > 0 ? (
                                        <div className="cw-dc-divider cw-dc-divider--section" />
                                    ) : null}
                                    <div className="cw-dc-details-groups">
                                        {renderDetailsForm(kind, {
                                            entry,
                                            heading: fileDetailsHeading(
                                                kind,
                                                kindIndex,
                                                fileDisplayName(entry)
                                            ),
                                        })}
                                    </div>
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <div className="cw-dc-details-groups">
                            {showSharedColumnsWarning ? (
                                <AlertBanner
                                    feedback="warning"
                                    text={SHARED_COLUMNS_WARNING}
                                    showButton={false}
                                    showCloseButton={false}
                                />
                            ) : null}
                            {renderDetailsForm(primaryDetailsTarget, {
                                suffix: splitBenchmarkDetails ? '(Dataset)' : '',
                            })}
                        </div>
                    )}
                    {showShareDetailsToggle && (
                        <>
                            <div className="cw-dc-divider cw-dc-divider--section" />
                            <div className="cw-dc-share-details">
                                <h3 className="cw-dc-section-title">Data Details</h3>
                                <ShareDetailsToggle
                                    checked={shareDetailsChecked}
                                    disabled={isComparingSharedLabels}
                                    onChange={handleShareDetailsChange}
                                />
                                {isComparingSharedLabels ? (
                                    <div
                                        className="cw-dc-mapping-loading"
                                        role="status"
                                        aria-live="polite"
                                    >
                                        <Spinner size="small" />
                                        <span>
                                            {isNer
                                                ? 'Extracting entities…'
                                                : 'Extracting labels…'}
                                        </span>
                                    </div>
                                ) : shareDetailsError ? (
                                    <div className="cw-field-error" role="alert">
                                        {shareDetailsError}
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                    {splitBenchmarkDetails && (
                        <>
                            <div className="cw-dc-divider cw-dc-divider--section" />
                            <div className="cw-dc-details-groups">
                                {renderDetailsForm('benchmark', { suffix: '(Benchmark)' })}
                            </div>
                        </>
                    )}
                </>
            )}

            {isModal && (
                <div className="cw-dc-modal-footer">
                    <Button
                        variant="primary"
                        size="medium"
                        disabled={nextLoading || trajectoriesNeedProvider}
                        disableWhenReadOnly
                        startIcon={nextLoading ? <Spinner size="small" /> : undefined}
                        onClick={handleNext}
                    >
                        {nextLoading ? nextLoadingLabel : 'Done'}
                    </Button>
                </div>
            )}

            <OptimizeOnLabelMissingModal
                mismatch={optimizeOnMismatch}
                surface="data-collection"
                onClose={() => setOptimizeOnMismatch(null)}
            />
            <UnknownLabelsWarningModal
                open={showUnknownLabelsModal}
                sources={unmatchedLabelSources}
                isNer={isNer}
                onCancel={() => setShowUnknownLabelsModal(false)}
                onContinue={continuePastUnknownLabels}
            />
            <NoProviderWarningModal
                open={showNoProviderModal}
                taskType={taskType}
                onCancel={() => setShowNoProviderModal(false)}
                onAddCredentials={handleAddCredentials}
            />
            {!isModal && (
                <ContinueWithoutBenchmarkModal
                    open={showBenchmarkModal}
                    onCancel={() => setShowBenchmarkModal(false)}
                    onContinue={proceed}
                />
            )}
            <input
                ref={addFilesInputRef}
                type="file"
                multiple
                accept={acceptAttributeForTaskType(taskType, { isAgentic })}
                className="cw-dc-add-files-input"
                aria-hidden="true"
                tabIndex={-1}
                onChange={(event) => {
                    const files = event.target.files;
                    const target = addFilesTargetRef.current;
                    if (files?.length && target) {
                        void upload.pickFiles(target, files);
                    }
                    event.target.value = '';
                }}
            />
        </div>
    );
};

export default DataCollectionPanel;
